import React, { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator } from "@/components/ui/context-menu";
import { PageSkeleton, ProjectViewSkeleton } from "@/components/LoadingSkeleton";
import { toast } from "sonner";
import {
  Plus, ExternalLink, ClipboardPaste, Trash2, Search, Filter, X, History, Pencil, Check,
  ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, LayoutList, KanbanSquare, Columns3, MoreHorizontal, Copy, Eye,
  Download, Upload, Settings2, AlertTriangle, Sparkles, TrendingUp, Palette
} from "lucide-react";
import BoardView from "@/components/BoardView";
import RefreshControl from "@/components/RefreshControl";
import useSheetKit from "@/hooks/useSheetKit";
import { HeaderMenu, BulkUpdateColumnDialog } from "@/components/SheetHeaderKit";
import ImportCSVDialog from "@/components/ImportCSVDialog";
import EditProjectDialog from "@/components/EditProjectDialog";
import CustomCell from "@/components/CustomCell";
import RaiseIssueDialog from "@/components/RaiseIssueDialog";
import { IssueTaskBanner, IssueHistoryPanel } from "@/components/IssueViews";
import { downloadCSV, stringify as csvStringify } from "@/lib/csv";

const STATUSES = ["To-Do", "Editing Pending", "Publishing Pending", "Review Pending", "Published"];
/* Iter 45.35 — Status is now a rounded-pill chip with a coloured dot.
   STATUS_CLS covers the pill background / text / border tone.
   STATUS_DOT is the vivid indicator that sits on the left of the pill. */
const STATUS_CLS = {
  "To-Do":              "bg-slate-100 text-slate-700 border-slate-200",
  "Editing Pending":    "bg-amber-50 text-amber-800 border-amber-200",
  "Publishing Pending": "bg-orange-50 text-orange-800 border-orange-200",
  "Review Pending":     "bg-sky-50 text-sky-800 border-sky-200",
  "Published":          "bg-emerald-50 text-emerald-800 border-emerald-200",
};
const STATUS_DOT = {
  "To-Do":              "bg-slate-400",
  "Editing Pending":    "bg-amber-500",
  "Publishing Pending": "bg-orange-500",
  "Review Pending":     "bg-sky-500",
  "Published":          "bg-emerald-500",
};
const METHOD_OPTIONS = ["Human", "AI"];

/* Iter 45.26 — Row highlight palette (Google Sheets-style pastels).
   Selecting a swatch calls bulkPatch({ row_color: <key> }); the empty
   string clears the color. Backend validates against ALLOWED_ROW_COLORS. */
const ROW_COLOR_PALETTE = {
  yellow: "#fff7ae",
  green:  "#d4edbc",
  blue:   "#c9e1f9",
  pink:   "#ffcfe0",
  purple: "#e4d3f5",
  orange: "#ffd8b1",
  red:    "#f9c9c9",
  gray:   "#e2e3e5",
};
const ROW_COLOR_SWATCHES = [
  { key: "yellow", label: "Yellow" },
  { key: "green",  label: "Green"  },
  { key: "blue",   label: "Blue"   },
  { key: "pink",   label: "Pink"   },
  { key: "purple", label: "Purple" },
  { key: "orange", label: "Orange" },
  { key: "red",    label: "Red"    },
  { key: "gray",   label: "Gray"   },
];

function formatRelative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/* Iter 45.25 — small circular avatar for the header collaborator stack.
   Uses initials with a deterministic tint hashed from the user id so the
   same teammate always shows up in the same colour across projects. */
const COLLAB_PALETTE = [
  "bg-violet-500", "bg-indigo-500", "bg-sky-500", "bg-emerald-500",
  "bg-amber-500",  "bg-rose-500",   "bg-fuchsia-500", "bg-lime-500",
  "bg-blue-500",   "bg-teal-500",   "bg-pink-500",  "bg-orange-500",
];
function _hash(s) {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff;
  return Math.abs(h);
}
function CollabAvatar({ user, isActive }) {
  const initials = (user?.name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const tint = COLLAB_PALETTE[_hash(user?.id || user?.name || "") % COLLAB_PALETTE.length];
  /* Iter 45.34 — small blue "currently viewing" dot at the bottom-right
     when the teammate is present on this project sheet right now.
     Rendered inside a `relative` wrapper so the dot floats over the
     avatar without shifting the stack's -space-x layout. */
  const dot = isActive ? (
    <span
      className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-blue-500 ring-2 ring-white shadow-sm"
      data-testid={`collab-active-dot-${user?.id}`}
      title={`${user?.name || "Someone"} is currently viewing this project`}
    />
  ) : null;
  if (user?.avatar_url) {
    return (
      <span className="relative inline-block shrink-0">
        <img
          src={user.avatar_url}
          alt={user.name}
          title={user.name}
          data-testid={`collab-avatar-${user.id}`}
          className="h-7 w-7 rounded-full ring-2 ring-white object-cover shadow-sm"
        />
        {dot}
      </span>
    );
  }
  return (
    <span className="relative inline-block shrink-0">
      <span
        title={user?.name}
        data-testid={`collab-avatar-${user?.id}`}
        className={`h-7 w-7 rounded-full ring-2 ring-white text-white text-[10px] font-bold flex items-center justify-center shadow-sm ${tint}`}
      >
        {initials}
      </span>
      {dot}
    </span>
  );
}

export default function ProjectView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [columns, setColumns] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("projectView") || "list");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkPosition, setBulkPosition] = useState("top");
  const [bulkDefaults, setBulkDefaults] = useState({ status: "", method: "", writer_id: "", publisher_id: "", niche: "", date: "" });
  // Iter 42 — default to "bottom" (matches Google-Sheets / Excel spreadsheet
  // UX where new rows land at the end of the list). Users who prefer the
  // legacy "top" behaviour can pick it from the position dropdown; their
  // choice is persisted in localStorage.
  const [addPosition, setAddPosition] = useState(() => localStorage.getItem("addPosition") || "bottom");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ status: "", writer_id: "", publisher_id: "", urgent: false });
  // Iter 42.7 — user-toggleable sort on the "Last Updated" column. Cycle:
  // null (default: issues pinned to top) → "desc" (newest first) → "asc"
  // (oldest first) → null. Persisted per project so a writer's preference
  // survives navigation.
  const [sortByUpdated, setSortByUpdated] = useState(() => localStorage.getItem(`pluto.sortByUpdated.${id}`) || null);
  useEffect(() => {
    if (sortByUpdated) localStorage.setItem(`pluto.sortByUpdated.${id}`, sortByUpdated);
    else localStorage.removeItem(`pluto.sortByUpdated.${id}`);
  }, [sortByUpdated, id]);
  const toggleSortByUpdated = () => {
    setSortByUpdated((s) => (s === null ? "desc" : s === "desc" ? "asc" : null));
  };
  const [editingCell, setEditingCell] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [editingProjectName, setEditingProjectName] = useState(false);
  const [projectNameDraft, setProjectNameDraft] = useState("");
  const [colDialog, setColDialog] = useState(null); // {mode: 'add'|'edit', col?}
  const [bulkUpdateCol, setBulkUpdateCol] = useState(null); // {colDef} | null
  const [importCsvOpen, setImportCsvOpen] = useState(false);
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [colPrefs, setColPrefs] = useState({ aliases: {}, hidden: [], order: [] });
  const [renamingColKey, setRenamingColKey] = useState(null); // col.key that is being renamed inline

  // Issues (Raise Issue feature) — summary keyed by task id, plus dialog state
  const [issuesByTask, setIssuesByTask] = useState({}); // { [task_id]: [issue, ...] }
  const [raiseIssueFor, setRaiseIssueFor] = useState(null); // task | null
  // Iter 37 — multi-position aware. A user is allowed to raise issues if
  // their PRIMARY role OR ANY position they hold matches. This lets a
  // "Content TL + Department Admin SEO" raise as a TL, and a plain
  // Publisher associate raise on their tasks.
  const allRoles = new Set([
    user?.role,
    ...((user?.positions || []).map((p) => p?.role).filter(Boolean)),
  ]);
  const allPositionTitles = new Set(
    (user?.positions || []).map((p) => (p?.position || "").toLowerCase().trim()).filter(Boolean),
  );
  const canRaiseIssue = (
    allRoles.has("admin") || allRoles.has("super_admin") || allRoles.has("department_admin")
    || allRoles.has("content_manager") || allRoles.has("team_lead") || allRoles.has("associate")
    || allPositionTitles.has("publisher") || allPositionTitles.has("publishing team lead")
    || allPositionTitles.has("content team lead") || allPositionTitles.has("writer") || allPositionTitles.has("editor")
  );

  const isAdmin = (user?.role === "admin" || user?.role === "super_admin");
  const canEditProject = (user?.role === "admin" || user?.role === "super_admin") || user?.role === "content_manager" || user?.role === "team_lead";
  const canDelete = (user?.role === "admin" || user?.role === "super_admin") || user?.role === "content_manager" || user?.role === "team_lead";
  const canCreate = canDelete;
  const canBulk = canDelete;

  // Iter 42.5.3 — Mark Productivity moved to the global profile menu
  // (see AppLayout.js). "See Productivity" stays project-scoped because
  // it deep-links to the report filtered to THIS project.
  const canSeeProductivity = !!project && (
    isAdmin || user?.role === "department_admin" || user?.role === "content_manager" || user?.role === "team_lead"
  );

  const dragRef = useRef({ dragging: false, startIdx: null, initial: null });
  const [lastClickedIdx, setLastClickedIdx] = useState(null);
  // Iter 45.34 — Live presence: user IDs currently viewing this project sheet.
  const [activeUserIds, setActiveUserIds] = useState(() => new Set());
  // Guards against stale reload responses overwriting a newer project. See
  // ScopeView for full explanation — same class of race, same fix.
  const activeProjectRef = useRef(id);
  useEffect(() => { activeProjectRef.current = id; }, [id]);

  // Iter 45.34 — Presence heartbeat.  Ping every 20 s, then fetch the fresh
  // active-user list.  Errors are silently swallowed — presence is a
  // nice-to-have overlay; a network hiccup should not interrupt the sheet.
  useEffect(() => {
    if (!id) return;
    let alive = true;
    const tick = async () => {
      try {
        await api.post(`/projects/${id}/presence/ping`);
        const { data } = await api.get(`/projects/${id}/presence`);
        if (alive) setActiveUserIds(new Set(data?.active_user_ids || []));
      } catch { /* silent — presence is optional */ }
    };
    tick();
    const iv = setInterval(tick, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [id]);

  const reload = async () => {
    const requestId = id;
    const [p, t, u, c] = await Promise.all([
      api.get(`/projects/${id}`),
      // Iter 42.9 — request the full task list (up to 20k) and strip
      // `status_history` from the wire payload. Silent truncation at 5000
      // hid users' data; history is fetched lazily by LastUpdatedCell on
      // popover open. On a 15k-task project this drops the JSON payload
      // roughly 3x versus the old default.
      api.get(`/tasks?project_id=${id}&limit=20000&include_history=false`),
      api.get("/users"),
      api.get(`/projects/${id}/columns`),  // backend serves department columns via this route
    ]);
    if (activeProjectRef.current !== requestId) return;
    setProject(p.data);
    setTasks(t.data);
    setUsers(u.data);
    setColumns(c.data || []);
    // Fetch column prefs for this department
    try {
      const dept = p.data?.department || "content";
      const { data: prefs } = await api.get(`/departments/${dept}/column-prefs`);
      if (activeProjectRef.current !== requestId) return;
      setColPrefs({ aliases: prefs.aliases || {}, hidden: prefs.hidden || [], order: prefs.order || [] });
    } catch { /* non-blocking */ }
    // Fetch open-issues summary (drives shaking banner + task pinning)
    try {
      const { data: summary } = await api.get(`/projects/${id}/issues/summary`);
      if (activeProjectRef.current !== requestId) return;
      setIssuesByTask(summary?.open_by_task || {});
    } catch { /* non-blocking */ }
  };

  const reloadIssues = async () => {
    try {
      const { data: summary } = await api.get(`/projects/${id}/issues/summary`);
      setIssuesByTask(summary?.open_by_task || {});
    } catch { /* ignore */ }
  };

  // Iter 45.21 — Clear stale project state on route change so the
  // <ProjectViewSkeleton /> loader fires IMMEDIATELY when navigating
  // between projects. Without this reset, the previous project's
  // spreadsheet stayed on-screen for the entire fetch window.
  useEffect(() => {
    setProject(null);
    setTasks([]);
    setColumns([]);
    setIssuesByTask({});
    setSelected(new Set());
    reload();
    /* eslint-disable-next-line */
  }, [id]);
  useEffect(() => { localStorage.setItem("projectView", view); }, [view]);
  useEffect(() => { localStorage.setItem("addPosition", addPosition); }, [addPosition]);

  useEffect(() => {
    const onCols = () => reload();
    window.addEventListener("columns-changed", onCols);
    return () => window.removeEventListener("columns-changed", onCols);
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    const onUp = () => { dragRef.current.dragging = false; };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  // Iter 45.25 (User ask #2) — Google-Sheets-style collaborator avatar
  // stack in the header. Anyone who's been given a "role" on this project
  // (content manager, team lead, writer, publisher, editor) has access to
  // the sheet; we render the union as a small overlapping avatar stack.
  // Only relies on fields already on the `project` payload so we don't
  // need an extra roundtrip — every viewer sees the same list even if
  // they don't have permission to call `/api/shares`.
  //
  // Iter 45.26 — attach the role this person plays on THIS project so the
  // header popover can show a proper "Sarah Miller · Content Manager"
  // line rather than opening the Edit Project dialog on click.
  const collaborators = useMemo(() => {
    if (!project) return [];
    // Priority order = the label that wins if a person holds multiple
    // roles on this project (rare in practice; e.g. a CM who also picked
    // themselves as TL). Higher first.
    const roleMap = new Map(); // uid -> role label
    const setIfHigher = (uid, label, rank) => {
      if (!uid) return;
      const prev = roleMap.get(uid);
      if (!prev || rank > prev.rank) roleMap.set(uid, { label, rank });
    };
    setIfHigher(project.content_manager_id, "Content Manager", 5);
    for (const uid of (project.content_manager_ids || [])) setIfHigher(uid, "Content Manager", 5);
    for (const uid of (project.team_lead_ids       || [])) setIfHigher(uid, "Team Lead",       4);
    for (const uid of (project.editor_ids          || [])) setIfHigher(uid, "Editor",          3);
    for (const uid of (project.publisher_ids       || [])) setIfHigher(uid, "Publisher",       2);
    for (const uid of (project.writer_ids          || [])) setIfHigher(uid, "Writer",          1);

    const out = [];
    for (const [uid, meta] of roleMap.entries()) {
      const u = userMap[uid];
      if (u && u.is_active !== false) out.push({ ...u, project_role: meta.label });
    }
    // Sort by role rank so the popover reads top-down: CM → TL → Editor → Publisher → Writer
    const rank = { "Content Manager": 5, "Team Lead": 4, "Editor": 3, "Publisher": 2, "Writer": 1 };
    out.sort((a, b) => (rank[b.project_role] ?? 0) - (rank[a.project_role] ?? 0) || (a.name || "").localeCompare(b.name || ""));
    return out;
  }, [project, userMap]);
  // Iter 37 — scoped pools per role, driven by the project's own
  // assigned lists. If a project has no writers/publishers/editors yet,
  // the corresponding dropdown will be empty — assign at the project
  // level first (via EditProjectDialog) before assigning per-task.
  const writerPool = useMemo(
    () => (project?.writer_ids || []).map((id) => userMap[id]).filter(Boolean),
    [project?.writer_ids, userMap],
  );
  const publisherPool = useMemo(
    () => (project?.publisher_ids || []).map((id) => userMap[id]).filter(Boolean),
    [project?.publisher_ids, userMap],
  );
  const editorPool = useMemo(
    () => (project?.editor_ids || []).map((id) => userMap[id]).filter(Boolean),
    [project?.editor_ids, userMap],
  );
  // Broader "everyone on this project" pool — used only where the bulk
  // action needs to hit any assignable regardless of column (e.g. row
  // context menu). Keeps the legacy behaviour for existing callers.
  const assignables = useMemo(() => {
    const ids = new Set([
      ...(project?.writer_ids || []),
      ...(project?.publisher_ids || []),
      ...(project?.editor_ids || []),
      ...(project?.team_lead_ids || []),
      ...(project?.content_manager_ids || []),
    ]);
    return Array.from(ids).map((id) => userMap[id]).filter(Boolean);
  }, [project, userMap]);

  const filtered = useMemo(() => tasks.filter((t) => {
    if (search && !(`${t.keyword} ${t.niche} ${t.notes}`.toLowerCase().includes(search.toLowerCase()))) return false;
    if (filters.status && t.status !== filters.status) return false;
    if (filters.writer_id && t.writer_id !== filters.writer_id) return false;
    if (filters.publisher_id && t.publisher_id !== filters.publisher_id) return false;
    // Iter 45.39 — Urgent quick-filter pill (below the pipeline bar) toggles
    // this flag; keeps rows only if the task's priority is truthy.
    if (filters.urgent && !t.priority) return false;
    return true;
  }), [tasks, search, filters]);

  // Iter 45.25 (User ask #1) — Deterministic row order across every viewer.
  // Previously the sheet did two things that made row positions differ from
  // user to user:
  //   (a) it pinned tasks with open issues to the top, and
  //   (b) the "Last Updated" sort was persisted per-user in localStorage.
  // For (a) — visibility of an issue can differ by role, so users would see
  // different rows first.  For (b) — different admins pick different sorts.
  // Both broke "row N is the same row for everyone".
  //
  // Fix: when the user has NOT explicitly turned on the Last-Updated sort,
  // sort by the canonical `order` field (server-authoritative, shared).
  // With a `created_at` + `id` tiebreaker the order is fully stable, so
  // row 5 is row 5 for every teammate.  The user-toggleable sort still
  // works — it's an opt-in local override.
  const filteredSorted = useMemo(() => {
    // Iter 45.39 — Urgent (priority=true) rows always float to the top
    // for every viewer, regardless of the current sort mode.  This
    // matches the backend's compound index (`priority DESC, order ASC`)
    // and gives one canonical "urgent first" view for the whole team.
    const priorityCmp = (a, b) => (a.priority ? 0 : 1) - (b.priority ? 0 : 1);
    if (sortByUpdated) {
      const dir = sortByUpdated === "asc" ? 1 : -1;
      const toTs = (t) => (t.updated_at ? new Date(t.updated_at).getTime() : 0);
      return [...filtered].sort((a, b) => priorityCmp(a, b) || (toTs(a) - toTs(b)) * dir);
    }
    return [...filtered].sort((a, b) => {
      const p = priorityCmp(a, b);
      if (p !== 0) return p;
      const ao = a.order ?? 0, bo = b.order ?? 0;
      if (ao !== bo) return ao - bo;
      const at = a.created_at || "", bt = b.created_at || "";
      if (at !== bt) return at < bt ? -1 : 1;
      return (a.id || "").localeCompare(b.id || "");
    });
  }, [filtered, sortByUpdated]);

  // Iter 42.4 (Perf Round 2) — row virtualization. `@tanstack/react-virtual`
  // renders only the ~30 rows currently in the viewport regardless of how
  // many total rows exist. Everything else (sheet-kit range math,
  // ContextMenu, autofill) uses row indices that match virtualRow.index,
  // so features stay fully functional at 10k+ tasks.
  const scrollContainerRef = useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredSorted.length,
    getScrollElement: () => scrollContainerRef.current,
    // Rows have `content-visibility: auto; contain-intrinsic-size: auto 40px;`
    // in CSS — matches sheet row height.
    //
    // Iter 45.22 — bumped overscan from 12 → 30 so the row buffer above
    // and below the viewport is deep enough to render before it enters
    // view during a fast momentum flick. Combined with the .smooth-scroll
    // CSS on the container this gives the sheet an iOS/Android-style
    // glide instead of the "row pop-in" feel it had with a 12-row buffer.
    estimateSize: () => 40,
    overscan: 30,
    // Stable key = task id so React can efficiently reconcile even when
    // filtered order changes (e.g. after a priority pin bumps rows up).
    getItemKey: (index) => filteredSorted[index]?.id ?? index,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const virtualTotalSize = rowVirtualizer.getTotalSize();
  const virtualPaddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const virtualPaddingBottom = virtualItems.length > 0
    ? virtualTotalSize - virtualItems[virtualItems.length - 1].end
    : 0;

  const tasksWithOpenIssues = useMemo(
    () => filtered.filter((t) => Array.isArray(issuesByTask[t.id]) && issuesByTask[t.id].length > 0),
    [filtered, issuesByTask]
  );

  // -------- Google-Sheets-like column definitions ---------
  const columnDefs = useMemo(() => {
    const findUserByName = (name) => assignables.find((u) => u.name.toLowerCase() === (name || "").trim().toLowerCase());
    const parseSelectOpts = (options) => (text) => {
      const s = (text ?? "").trim();
      if (s === "") return "";
      const found = options.find((o) => o.toLowerCase() === s.toLowerCase());
      return found ?? undefined;
    };
    const base = [
      { key: "priority", label: "Priority", type: "priority", editable: canBulk,
        getVal: (t) => !!t.priority, setValInto: (v) => ({ priority: !!v }),
        textOf: (t) => (t.priority ? "PRIORITY" : ""),
        parseText: (text) => {
          const s = (text || "").trim().toLowerCase();
          if (["true", "1", "yes", "priority", "high", "red"].includes(s)) return true;
          if (["false", "0", "no", ""].includes(s)) return false;
          return undefined;
        },
      },
      { key: "date", label: "Date", type: "date", editable: canBulk,
        getVal: (t) => t.date || "", setValInto: (v) => ({ date: v }),
        textOf: (t) => t.date || "", parseText: (text) => (text ?? "").trim() },
      { key: "keyword", label: "Keyword", type: "text", editable: canBulk,
        getVal: (t) => t.keyword || "", setValInto: (v) => ({ keyword: v }),
        textOf: (t) => t.keyword || "", parseText: (text) => text ?? "" },
      { key: "niche", label: "Niche", type: "text", editable: canBulk,
        getVal: (t) => t.niche || "", setValInto: (v) => ({ niche: v }),
        textOf: (t) => t.niche || "", parseText: (text) => text ?? "" },
      { key: "ai_score", label: "AI Score", type: "text", editable: canBulk,
        getVal: (t) => t.ai_score || "", setValInto: (v) => ({ ai_score: v }),
        textOf: (t) => (t.ai_score ?? "").toString(), parseText: (text) => text ?? "" },
      { key: "draft", label: "Draft", type: "url", editable: canBulk,
        getVal: (t) => t.draft || "", setValInto: (v) => ({ draft: v }),
        textOf: (t) => t.draft || "", parseText: (text) => text ?? "" },
      { key: "method", label: "Method", type: "select", options: METHOD_OPTIONS, editable: canBulk,
        getVal: (t) => t.method || "", setValInto: (v) => ({ method: v }),
        textOf: (t) => t.method || "", parseText: parseSelectOpts(METHOD_OPTIONS) },
      { key: "writer_id", label: "Writer", type: "user", editable: canBulk, userList: assignables,
        getVal: (t) => t.writer_id || null,
        setValInto: (v) => ({ writer_id: v || null }),
        textOf: (t, ctx) => ctx?.userMap?.[t.writer_id]?.name || "",
        parseText: (text) => {
          const s = (text || "").trim();
          if (s === "" || s.toLowerCase() === "unassigned") return null;
          return findUserByName(s)?.id ?? undefined;
        } },
      { key: "publisher_id", label: "Publisher", type: "user", editable: canBulk, userList: assignables,
        getVal: (t) => t.publisher_id || null,
        setValInto: (v) => ({ publisher_id: v || null }),
        textOf: (t, ctx) => ctx?.userMap?.[t.publisher_id]?.name || "",
        parseText: (text) => {
          const s = (text || "").trim();
          if (s === "" || s.toLowerCase() === "unassigned") return null;
          return findUserByName(s)?.id ?? undefined;
        } },
      { key: "status", label: "Status", type: "select", options: STATUSES, editable: canBulk,
        getVal: (t) => t.status || "", setValInto: (v) => ({ status: v }),
        textOf: (t) => t.status || "", parseText: parseSelectOpts(STATUSES) },
      { key: "check", label: "Check", type: "bool", editable: canBulk,
        getVal: (t) => !!t.check, setValInto: (v) => ({ check: !!v }),
        textOf: (t) => t.check ? "TRUE" : "",
        parseText: (text) => {
          const s = (text || "").trim().toLowerCase();
          if (["true","1","yes","x","✓"].includes(s)) return true;
          if (["false","0","no",""].includes(s)) return false;
          return undefined;
        } },
      { key: "notes", label: "Notes", type: "text", editable: canBulk,
        getVal: (t) => t.notes || "", setValInto: (v) => ({ notes: v }),
        textOf: (t) => t.notes || "", parseText: (text) => text ?? "" },
      { key: "docs", label: "Docs", type: "url", editable: canBulk,
        getVal: (t) => t.docs || "", setValInto: (v) => ({ docs: v }),
        textOf: (t) => t.docs || "", parseText: (text) => text ?? "" },
    ];
    const custom = (columns || []).map((c) => ({
      key: `custom.${c.id}`, colId: c.id, label: c.label, type: c.type,
      options: c.options, isCustom: true, editable: canBulk,
      userList: c.type === "member" ? assignables : undefined,
      getVal: (t) => t.custom?.[c.id] ?? "",
      setValInto: (v, task) => ({ custom: { ...(task.custom || {}), [c.id]: v } }),
      textOf: (t, ctx) => {
        const v = t.custom?.[c.id];
        if (v == null || v === "") return "";
        if (c.type === "member") return ctx?.userMap?.[v]?.name || "";
        return String(v);
      },
      parseText: (text) => {
        const s = (text || "").trim();
        if (c.type === "dropdown") {
          if (s === "") return "";
          const found = (c.options || []).find((o) => o.toLowerCase() === s.toLowerCase());
          return found ?? undefined;
        }
        if (c.type === "member") {
          if (s === "" || s.toLowerCase() === "unassigned") return "";
          return findUserByName(s)?.id ?? undefined;
        }
        if (c.type === "number") return s === "" ? "" : (isNaN(Number(s)) ? undefined : Number(s));
        return s;
      },
    }));
    return [...base, ...custom];
  }, [columns, assignables, canBulk]);

  // Apply per-department prefs: alias labels, filter hidden, sort by order array
  const visibleColumnDefs = useMemo(() => {
    const aliased = columnDefs.map((c) => ({ ...c, label: colPrefs.aliases?.[c.key] ?? c.label }));
    const notHidden = aliased.filter((c) => !colPrefs.hidden?.includes(c.key));
    if (!colPrefs.order || colPrefs.order.length === 0) return notHidden;
    const keyPos = Object.fromEntries(colPrefs.order.map((k, i) => [k, i]));
    return [...notHidden].sort((a, b) => (keyPos[a.key] ?? 9999) - (keyPos[b.key] ?? 9999));
  }, [columnDefs, colPrefs]);

  // Save prefs helper (admin only). Sends only the fields present.
  const savePrefs = async (patch) => {
    const dept = project?.department || "content";
    setColPrefs((prev) => ({ ...prev, ...patch }));
    try {
      const { data } = await api.patch(`/departments/${dept}/column-prefs`, patch);
      setColPrefs({ aliases: data.aliases || {}, hidden: data.hidden || [], order: data.order || [] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ------- Sheet-kit: focus, keyboard, copy/paste, autofill, undo/redo -------
  const sheet = useSheetKit({
    rows: filteredSorted,
    columnDefs: visibleColumnDefs,
    canEdit: canBulk,
    context: { userMap },
    onPatchTask: async (task, patch) => {
      // Optimistic: apply locally immediately, then send in background
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...patch, updated_at: new Date().toISOString() } : t)));
      try {
        const { data } = await api.patch(`/tasks/${task.id}`, patch);
        setTasks((prev) => prev.map((t) => (t.id === task.id ? data : t)));
        return data;
      } catch (e) {
        setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
        toast.error(formatApiError(e));
        throw e;
      }
    },
    onBulkPatch: async (updates) => {
      if (!updates || updates.length === 0) return [];
      // Optimistic: apply all patches locally first
      const patchById = Object.fromEntries(updates.map((u) => [u.task_id, u.patch]));
      const beforeSnap = tasks.filter((t) => patchById[t.id]).map((t) => ({ ...t }));
      setTasks((prev) => prev.map((t) => (patchById[t.id] ? { ...t, ...patchById[t.id], updated_at: new Date().toISOString() } : t)));
      try {
        const { data } = await api.post("/tasks/bulk-patch-many", { updates });
        // Merge server-authoritative tasks
        const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
        setTasks((prev) => prev.map((t) => byId[t.id] || t));
        return data.tasks || [];
      } catch (e) {
        // Rollback
        setTasks((prev) => { const bi = Object.fromEntries(beforeSnap.map((t) => [t.id, t])); return prev.map((t) => bi[t.id] || t); });
        toast.error(formatApiError(e));
        throw e;
      }
    },
    onCreateTask: async (partial = {}) => {
      const body = { project_id: id, keyword: "", ...partial };
      const { data } = await api.post("/tasks", body);
      setTasks((prev) => body.position === "bottom" ? [...prev, data] : [data, ...prev]);
      return data;
    },
    // Iter 42.4 (Perf Round 2 hotfix) — bulk-empty endpoint prevents the
    // 429 storm that used to hit when a paste needed 100+ new rows.
    onCreateManyTasks: async (count, position = "bottom") => {
      if (!count || count < 1) return [];
      const { data } = await api.post("/tasks/bulk-empty", { project_id: id, count, position });
      const created = data.tasks || [];
      if (created.length) {
        setTasks((prev) => position === "bottom" ? [...prev, ...created] : [...created, ...prev]);
      }
      return created;
    },
    onDeleteTasks: async (taskIds) => {
      await api.post("/tasks/bulk-delete", { task_ids: taskIds });
      setTasks((prev) => prev.filter((t) => !taskIds.includes(t.id)));
      setSelected((prev) => { const n = new Set(prev); taskIds.forEach((tid) => n.delete(tid)); return n; });
    },
  });

  // Bridge: sync sheet-kit editing coord → existing editingCell (task/field) state used by EditableCell
  useEffect(() => {
    if (!sheet.editing) { setEditingCell(null); return; }
    const col = visibleColumnDefs[sheet.editing.c];
    const task = filtered[sheet.editing.r];
    if (col && task && !col.isCustom) setEditingCell({ taskId: task.id, field: col.key });
    else setEditingCell(null);
    // eslint-disable-next-line
  }, [sheet.editing]);

  // Autofill: while a drag is active, listen to mousemove to extend, mouseup to commit
  useEffect(() => {
    if (!sheet.autofill) return;
    const onMove = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const td = el && el.closest ? el.closest("td[data-cell]") : null;
      if (!td) return;
      const parts = (td.dataset.cell || "").split("-");
      const r = Number(parts[0]);
      if (!Number.isNaN(r)) sheet.extendAutofill(r);
    };
    const onUp = async () => { await sheet.commitAutofill(); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp, { once: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line
  }, [sheet.autofill]);

  // Global paste-anywhere → creates new tasks. Deferred to sheet-kit when a cell is focused.
  useEffect(() => {
    if (!canBulk) return;
    const onPaste = async (e) => {
      if (sheet.focus) return; // sheet-kit handles cell-level paste
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
      const text = e.clipboardData?.getData("text/plain");
      if (!text || !text.trim()) return;
      e.preventDefault();
      try {
        const { data } = await api.post("/tasks/bulk", { project_id: id, raw: text, position: addPosition });
        toast.success(`Pasted ${data.created} task${data.created === 1 ? "" : "s"} at ${addPosition}`);
        // Merge created tasks locally — no reload cascade. Iter 36.9.11 —
        // re-sort by `order` after merging so the pasted rows always land
        // in the exact order the caller copied them (even if some existing
        // rows happen to have overlapping order values from an older run).
        if (data.tasks && data.tasks.length > 0) {
          setTasks((prev) => {
            const combined = addPosition === "top" ? [...data.tasks, ...prev] : [...prev, ...data.tasks];
            return [...combined].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          });
          const createdIds = data.tasks.map((t) => t.id);
          let liveIds = [...createdIds];
          sheet.pushUndo({
            label: `paste ${createdIds.length} tasks`,
            revert: async () => { try { await api.post("/tasks/bulk-delete", { task_ids: liveIds }); } catch {}; setTasks((prev) => prev.filter((t) => !liveIds.includes(t.id))); },
            redo: async () => { try { const { data: r } = await api.post("/tasks/bulk", { project_id: id, raw: text, position: addPosition }); if (r?.tasks) { liveIds = r.tasks.map((x) => x.id); setTasks((prev) => addPosition === "top" ? [...r.tasks, ...prev] : [...prev, ...r.tasks]); } } catch {} },
          });
        }
      } catch (err) { toast.error(formatApiError(err)); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line
  }, [id, canBulk, addPosition, sheet.focus]);

  const filteredIds = useMemo(() => filtered.map((t) => t.id), [filtered]);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = filteredIds.some((id) => selected.has(id));

  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      filteredIds.forEach((id) => next.delete(id));
      setSelected(next);
    } else setSelected(new Set([...selected, ...filteredIds]));
  };
  const toggleOne = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const startRowSelect = (idx, e) => {
    if (!canBulk) return;
    e.preventDefault();
    const additive = e.metaKey || e.ctrlKey;
    const rangeExtend = e.shiftKey && lastClickedIdx !== null;
    if (rangeExtend) {
      const from = Math.min(lastClickedIdx, idx);
      const to = Math.max(lastClickedIdx, idx);
      const next = new Set(selected);
      for (let i = from; i <= to; i++) if (filtered[i]) next.add(filtered[i].id);
      setSelected(next); return;
    }
    const base = additive ? new Set(selected) : new Set();
    const tid = filtered[idx]?.id;
    if (tid) { if (additive && selected.has(tid)) base.delete(tid); else base.add(tid); }
    setSelected(base); setLastClickedIdx(idx);
    dragRef.current = { dragging: true, startIdx: idx, initial: additive ? new Set(selected) : new Set() };
  };
  const dragOverRow = (idx) => {
    if (!dragRef.current.dragging) return;
    const from = Math.min(dragRef.current.startIdx, idx);
    const to = Math.max(dragRef.current.startIdx, idx);
    const next = new Set(dragRef.current.initial);
    for (let i = from; i <= to; i++) if (filtered[i]) next.add(filtered[i].id);
    setSelected(next);
  };

  // Undo-aware helpers
  const computeInverseFromTask = (task, patch) => {
    const inverse = {};
    for (const k of Object.keys(patch)) {
      if (k === "custom") {
        inverse.custom = { ...(task.custom || {}) };
        for (const ck of Object.keys(patch.custom || {})) inverse.custom[ck] = task.custom?.[ck] ?? "";
      } else {
        inverse[k] = task[k] ?? (typeof patch[k] === "boolean" ? false : (patch[k] === null ? null : ""));
      }
    }
    return inverse;
  };

  const addRow = async () => {
    try {
      const { data } = await api.post("/tasks", { project_id: id, keyword: "", position: addPosition });
      // Iter 42.4 (Perf Round 1) — insert the created task locally instead
      // of refetching the entire task list. On a 1000-task project this
      // cuts add-task latency from ~2s (full 2MB refetch) to ~100ms.
      // Backend already stamped `order` correctly so the local placement
      // (prepend for top, append for bottom) matches the server sort.
      setTasks((prev) => addPosition === "top" ? [data, ...prev] : [...prev, data]);
      let liveId = data.id;
      sheet.pushUndo({
        label: "add task",
        revert: async () => {
          try { await api.delete(`/tasks/${liveId}`); } catch {}
          setTasks((prev) => prev.filter((t) => t.id !== liveId));
        },
        redo: async () => {
          try {
            const { data: recreated } = await api.post("/tasks", { project_id: id, keyword: "", position: addPosition });
            if (recreated && recreated.id) {
              liveId = recreated.id;
              setTasks((prev) => addPosition === "top" ? [recreated, ...prev] : [...prev, recreated]);
            }
          } catch {}
        },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleBulk = async () => {
    if (!bulkText.trim()) return;
    try {
      const payload = { project_id: id, raw: bulkText, position: bulkPosition };
      // Only include set defaults
      Object.entries(bulkDefaults).forEach(([k, v]) => { if (v) payload[k] = v; });
      const { data } = await api.post("/tasks/bulk", payload);
      toast.success(`Added ${data.created} tasks at ${bulkPosition}`);
      setBulkOpen(false); setBulkText("");
      setBulkDefaults({ status: "", method: "", writer_id: "", publisher_id: "", niche: "", date: "" });
      const createdTasks = data.tasks || [];
      // Iter 42.4 (Perf Round 1) — insert locally instead of reload().
      // A bulk paste of 100 rows previously triggered a full refetch of
      // the whole project (potentially 5000 rows). Now we merge only the
      // returned created rows into local state.
      if (createdTasks.length > 0) {
        setTasks((prev) => bulkPosition === "top" ? [...createdTasks, ...prev] : [...prev, ...createdTasks]);
        let liveIds = createdTasks.map((t) => t.id);
        sheet.pushUndo({
          label: `bulk paste ${createdTasks.length} tasks`,
          revert: async () => {
            try { await api.post("/tasks/bulk-delete", { task_ids: liveIds }); } catch {}
            setTasks((prev) => prev.filter((t) => !liveIds.includes(t.id)));
          },
          redo: async () => {
            try {
              const { data: reBulk } = await api.post("/tasks/bulk", payload);
              const newTasks = reBulk.tasks || [];
              if (newTasks.length) {
                liveIds = newTasks.map((t) => t.id);
                setTasks((prev) => bulkPosition === "top" ? [...newTasks, ...prev] : [...prev, ...newTasks]);
              }
            } catch {}
          },
        });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- CSV Export / Import ----------
  const exportCsv = () => {
    if (filtered.length === 0) { toast.message("No tasks to export"); return; }
    const cols = visibleColumnDefs.filter((c) => c.textOf || typeof c.getVal === "function");
    const headers = cols.map((c) => c.label);
    const body = filtered.map((t) => cols.map((c) => (c.textOf ? c.textOf(t, { userMap }) : String(c.getVal(t) ?? ""))));
    const csvText = csvStringify([headers, ...body]);
    const projSlug = (project?.name || "tasks").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCSV(`${projSlug}-tasks-${stamp}.csv`, csvText);
    toast.success(`Exported ${filtered.length} task${filtered.length === 1 ? "" : "s"}`);
  };

  const importCsv = async ({ rows, position }) => {
    const payload = { project_id: id, rows, position };
    const { data } = await api.post("/tasks/bulk-import", payload);
    const created = data.tasks || [];
    if (created.length > 0) {
      setTasks((prev) => position === "top" ? [...created, ...prev] : [...prev, ...created]);
      let liveIds = created.map((t) => t.id);
      sheet.pushUndo({
        label: `import ${created.length} task${created.length === 1 ? "" : "s"}`,
        revert: async () => {
          try { await api.post("/tasks/bulk-delete", { task_ids: liveIds }); } catch {}
          setTasks((prev) => prev.filter((t) => !liveIds.includes(t.id)));
        },
        redo: async () => {
          try {
            const { data: re } = await api.post("/tasks/bulk-import", payload);
            const newTasks = re.tasks || [];
            if (newTasks.length) {
              liveIds = newTasks.map((t) => t.id);
              setTasks((prev) => position === "top" ? [...newTasks, ...prev] : [...prev, ...newTasks]);
            }
          } catch {}
        },
      });
    }
  };

  const updateTask = async (taskId, patch) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    const inverse = computeInverseFromTask(task, patch);
    // Optimistic update
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch, updated_at: new Date().toISOString() } : t)));
    try {
      const { data } = await api.patch(`/tasks/${taskId}`, patch);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data : t)));
      sheet.pushUndo({
        label: `edit ${Object.keys(patch).join(", ")}`,
        revert: async () => {
          setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...inverse } : t)));
          try {
            const { data: reverted } = await api.patch(`/tasks/${taskId}`, inverse);
            setTasks((prev) => prev.map((t) => (t.id === taskId ? reverted : t)));
          } catch {}
        },
        redo: async () => {
          setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
          try {
            const { data: redone } = await api.patch(`/tasks/${taskId}`, patch);
            setTasks((prev) => prev.map((t) => (t.id === taskId ? redone : t)));
          } catch {}
        },
      });
    } catch (e) {
      // Rollback optimistic update
      setTasks((prev) => prev.map((t) => (t.id === taskId ? task : t)));
      toast.error(formatApiError(e));
    }
  };

  // Delete key: only handles row-checkbox deletion (cell clearing is delegated to sheet-kit)
  useEffect(() => {
    if (!canBulk) return;
    const onKey = (e) => {
      if (!(e.key === "Delete" || e.key === "Backspace")) return;
      if (selected.size === 0) return;  // no row selection → let sheet-kit clear focused cell
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      bulkDelete();
    };
    window.addEventListener("keydown", onKey, true); // capture phase — preempts sheet-kit
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line
  }, [canBulk, selected]);

  const removeTask = async (taskId) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    let liveId = taskId; // will be reassigned on undo if we re-create
    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      setSelected((prev) => { const n = new Set(prev); n.delete(taskId); return n; });
      sheet.pushUndo({
        label: "delete task",
        revert: async () => {
          const { id: _oid, created_at, updated_at, order, ...rest } = task;
          const { data } = await api.post("/tasks", { ...rest, project_id: id, position: "bottom" });
          setTasks((prev) => [...prev, data]);
          if (data && data.id) liveId = data.id;
        },
        redo: async () => {
          try { await api.delete(`/tasks/${liveId}`); } catch {}
          setTasks((prev) => prev.filter((t) => t.id !== liveId));
        },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const bulkPatch = async (patch) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const snapshotById = Object.fromEntries(tasks.filter((t) => ids.includes(t.id)).map((t) => [t.id, computeInverseFromTask(t, patch)]));
    // Optimistic: apply locally
    setTasks((prev) => prev.map((t) => (ids.includes(t.id) ? { ...t, ...patch, updated_at: new Date().toISOString() } : t)));
    try {
      const { data } = await api.post("/tasks/bulk-update", { task_ids: ids, patch });
      const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
      setTasks((prev) => prev.map((t) => byId[t.id] || t));
      toast.success(`Updated ${data.updated} tasks`);
      const invList = Object.entries(snapshotById).map(([task_id, p]) => ({ task_id, patch: p }));
      sheet.pushUndo({
        label: `bulk edit ${Object.keys(patch).join(", ")}`,
        revert: async () => { try { await api.post("/tasks/bulk-patch-many", { updates: invList }); } catch {}; reload(); },
        redo:   async () => { try { await api.post("/tasks/bulk-update", { task_ids: ids, patch }); } catch {}; reload(); },
      });
    } catch (e) { toast.error(formatApiError(e)); reload(); }
  };
  const bulkDelete = async () => {
    if (!confirm(`Delete ${selected.size} tasks?`)) return;
    const ids = Array.from(selected);
    const snapshots = tasks.filter((t) => ids.includes(t.id));
    let liveIds = [...ids];
    // Optimistic — remove locally first
    setTasks((prev) => prev.filter((t) => !liveIds.includes(t.id)));
    setSelected(new Set());
    try {
      const { data } = await api.post("/tasks/bulk-delete", { task_ids: liveIds });
      toast.success(`Deleted ${data.deleted} tasks`);
      sheet.pushUndo({
        label: `delete ${snapshots.length} tasks`,
        revert: async () => {
          // Batch recreate in parallel — 10× faster than sequential
          const results = await Promise.all(snapshots.map((s) => {
            const { id: _oid, created_at, updated_at, order, ...rest } = s;
            return api.post("/tasks", { ...rest, project_id: id, position: "bottom" }).then((r) => r.data).catch(() => null);
          }));
          const newIds = [];
          const created = results.filter(Boolean);
          created.forEach((t) => newIds.push(t.id));
          if (newIds.length) liveIds = newIds;
          setTasks((prev) => [...prev, ...created]);
        },
        redo: async () => {
          try { await api.post("/tasks/bulk-delete", { task_ids: liveIds }); } catch {}
          setTasks((prev) => prev.filter((t) => !liveIds.includes(t.id)));
        },
      });
    } catch (e) {
      // Rollback on error
      setTasks((prev) => [...snapshots, ...prev]);
      toast.error(formatApiError(e));
    }
  };

  const saveProjectName = async () => {
    const name = projectNameDraft.trim();
    if (!name || name === project.name) { setEditingProjectName(false); return; }
    try {
      await api.patch(`/projects/${id}`, { name });
      toast.success("Project renamed"); setProject({ ...project, name });
    } catch (e) { toast.error(formatApiError(e)); }
    setEditingProjectName(false);
  };

  const updateCustom = (taskId, colId, value) => {
    const t = tasks.find((x) => x.id === taskId);
    if (!t) return;
    const newCustom = { ...(t.custom || {}), [colId]: value };
    updateTask(taskId, { custom: newCustom });
  };

  const deleteColumn = async (colId) => {
    if (!confirm("Delete this column across the whole department? All data in it will be removed from every project.")) return;
    try {
      const dept = project.department || "content";
      await api.delete(`/departments/${dept}/columns/${colId}`);
      toast.success("Column deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyBulkUpdate = async (col, newValue, limit) => {
    const targets = filtered.slice(0, limit);
    if (targets.length === 0) return;
    // Iter 42 — clear any active cell focus / range before applying so
    // that when the dialog closes the user can immediately click a
    // specific cell to edit its value individually (previously the
    // spreadsheet's stale range/focus was blocking the next click).
    sheet.setFocusFromRow(-1, 0);
    // Build ONE patch (same value across targets) — use fast /tasks/bulk-update
    const samplePatch = col.setValInto(newValue, targets[0]);
    const isCustomPatch = "custom" in samplePatch;
    if (!isCustomPatch) {
      // Same-value bulk-update path
      const patch = samplePatch;
      const inverseById = Object.fromEntries(targets.map((t) => [t.id, col.setValInto(col.getVal(t), t)]));
      // Optimistic
      setTasks((prev) => prev.map((t) => (inverseById[t.id] ? { ...t, ...patch, updated_at: new Date().toISOString() } : t)));
      try {
        const { data } = await api.post("/tasks/bulk-update", { task_ids: targets.map((t) => t.id), patch });
        const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
        setTasks((prev) => prev.map((t) => byId[t.id] || t));
        toast.success(`Updated ${data.updated} cell${data.updated === 1 ? "" : "s"}`);
        const invList = Object.entries(inverseById).map(([task_id, p]) => ({ task_id, patch: p }));
        sheet.pushUndo({
          label: `bulk update ${col.label}`,
          revert: async () => { try { await api.post("/tasks/bulk-patch-many", { updates: invList }); } catch {} },
          redo:   async () => { try { await api.post("/tasks/bulk-update", { task_ids: targets.map((t) => t.id), patch }); } catch {} },
        });
      } catch (e) {
        toast.error(formatApiError(e));
      }
      return;
    }
    // Custom-field path — must use bulk-patch-many (each task has its own custom object)
    const updates = targets.map((t) => ({ task_id: t.id, patch: col.setValInto(newValue, t) }));
    const inverses = targets.map((t) => ({ task_id: t.id, patch: col.setValInto(col.getVal(t), t) }));
    // Optimistic
    setTasks((prev) => { const byId = Object.fromEntries(updates.map((u) => [u.task_id, u.patch])); return prev.map((t) => byId[t.id] ? { ...t, ...byId[t.id], updated_at: new Date().toISOString() } : t); });
    try {
      const { data } = await api.post("/tasks/bulk-patch-many", { updates });
      const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
      setTasks((prev) => prev.map((t) => byId[t.id] || t));
      toast.success(`Updated ${data.updated} cell${data.updated === 1 ? "" : "s"}`);
      sheet.pushUndo({
        label: `bulk update ${col.label}`,
        revert: async () => { try { await api.post("/tasks/bulk-patch-many", { updates: inverses }); } catch {} },
        redo:   async () => { try { await api.post("/tasks/bulk-patch-many", { updates }); } catch {} },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter 45.23 — Auto-fit column widths from the LONGEST content in each
  // column across the entire filtered task set (not just the ~30 rows
  // currently in the DOM under virtualization). We measure the string
  // returned by `col.textOf(task, ctx)` for every task plus the header
  // label, convert char length → pixels with a ~7.2px average + 40px of
  // cell chrome (padding, sort arrow, menu icon), and clamp to
  // [90px, 480px] so nothing shrinks below usability or stretches to eat
  // the viewport. Applied as an inline `style` on each header <th>; cells
  // inherit the width via the browser's table auto-layout.
  const CHAR_PX = 7.2;
  const COL_CHROME_PX = 40;
  const COL_MIN_PX = 90;
  const COL_MAX_PX = 480;
  const colWidths = useMemo(() => {
    const ctx = { userMap };
    const widths = {};
    for (const col of visibleColumnDefs) {
      let maxLen = (col.label || "").length;
      if (col.textOf) {
        for (const t of filteredSorted) {
          const s = String(col.textOf(t, ctx) ?? "");
          if (s.length > maxLen) maxLen = s.length;
        }
      }
      let px = Math.round(maxLen * CHAR_PX + COL_CHROME_PX);
      if (px < COL_MIN_PX) px = COL_MIN_PX;
      if (px > COL_MAX_PX) px = COL_MAX_PX;
      widths[col.key] = px;
    }
    return widths;
    // eslint-disable-next-line
  }, [visibleColumnDefs, filteredSorted, userMap]);
  const colStyleFor = (col) => {
    const w = colWidths[col.key] || COL_MIN_PX;
    return { width: `${w}px`, minWidth: `${w}px` };
  };

  // Drag-to-reorder handlers
  const [dragOverKey, setDragOverKey] = useState(null);
  const handleColDragStart = (e, col) => {
    e.dataTransfer.setData("text/sheet-col-key", col.key);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleColDragOver = (e, col) => {
    if (!e.dataTransfer.types.includes("text/sheet-col-key")) return;
    e.preventDefault();
    setDragOverKey(col.key);
  };
  const handleColDrop = async (e, targetCol) => {
    e.preventDefault();
    setDragOverKey(null);
    const fromKey = e.dataTransfer.getData("text/sheet-col-key");
    if (!fromKey || fromKey === targetCol.key) return;
    const visibleKeys = visibleColumnDefs.map((c) => c.key);
    const fromIdx = visibleKeys.indexOf(fromKey);
    const toIdx = visibleKeys.indexOf(targetCol.key);
    if (fromIdx < 0 || toIdx < 0) return;
    const newVisible = [...visibleKeys];
    newVisible.splice(fromIdx, 1);
    newVisible.splice(toIdx, 0, fromKey);
    // Preserve hidden keys at end so they retain relative order if un-hidden
    const hiddenKeys = columnDefs.filter((c) => colPrefs.hidden?.includes(c.key)).map((c) => c.key);
    await savePrefs({ order: [...newVisible, ...hiddenKeys] });
  };

  // Handlers for menu actions on a given visible column
  const onColRename = (col) => setRenamingColKey(col.key);
  const commitColRename = async (col, newLabel) => {
    setRenamingColKey(null);
    const label = (newLabel || "").trim();
    if (!label || label === col.label) return;
    if (col.isCustom) {
      try {
        await api.patch(`/departments/${project.department || "content"}/columns/${col.colId}`, { label });
        toast.success("Column renamed everywhere");
        window.dispatchEvent(new CustomEvent("columns-changed"));
      } catch (e) { toast.error(formatApiError(e)); }
    } else {
      await savePrefs({ aliases: { ...(colPrefs.aliases || {}), [col.key]: label } });
      toast.success("Column renamed");
    }
  };
  const onColHideOrDelete = async (col) => {
    if (col.isCustom) {
      if (!confirm("Delete this column across the whole department? All data in it will be removed from every project.")) return;
      await deleteColumn(col.colId);
    } else {
      const nextHidden = [...(colPrefs.hidden || []), col.key];
      await savePrefs({ hidden: nextHidden });
      toast.success(`Hidden "${col.label}" — restore from the "Hidden" button`);
    }
  };
  const onColEditType = (col) => {
    if (!col.isCustom) return;
    const rawCol = columns.find((c) => c.id === col.colId);
    if (rawCol) setColDialog({ mode: "edit", col: rawCol });
  };
  const onColRestore = async (key) => {
    const nextHidden = (colPrefs.hidden || []).filter((k) => k !== key);
    await savePrefs({ hidden: nextHidden });
  };

  const emptyValueForCol = (col) => {
    switch (col.type) {
      case "bool": return false;
      case "number": return "";
      case "user":
      case "member": return null;
      case "select":
      case "dropdown": return "";
      default: return "";
    }
  };
  const onColClear = async (col) => {
    if (!col.editable) return;
    if (!confirm(`Clear all ${filtered.length} values in "${col.label}"?`)) return;
    const emptyVal = emptyValueForCol(col);
    // Only include tasks whose current value differs from the empty target
    const targets = filtered.filter((t) => col.getVal(t) !== emptyVal);
    if (targets.length === 0) { toast.message("Column is already empty"); return; }
    const patch = col.setValInto(emptyVal, targets[0]);
    const isCustomPatch = "custom" in patch;
    if (!isCustomPatch) {
      const ids = targets.map((t) => t.id);
      const inverseById = Object.fromEntries(targets.map((t) => [t.id, col.setValInto(col.getVal(t), t)]));
      setTasks((prev) => prev.map((t) => (inverseById[t.id] ? { ...t, ...patch } : t)));
      try {
        const { data } = await api.post("/tasks/bulk-update", { task_ids: ids, patch });
        const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
        setTasks((prev) => prev.map((t) => byId[t.id] || t));
        toast.success(`Cleared ${data.updated} cell${data.updated === 1 ? "" : "s"}`);
        const invList = Object.entries(inverseById).map(([task_id, p]) => ({ task_id, patch: p }));
        sheet.pushUndo({
          label: `clear ${col.label}`,
          revert: async () => { try { await api.post("/tasks/bulk-patch-many", { updates: invList }); } catch {} },
          redo:   async () => { try { await api.post("/tasks/bulk-update", { task_ids: ids, patch }); } catch {} },
        });
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    // Custom-col path
    const updates = targets.map((t) => ({ task_id: t.id, patch: col.setValInto(emptyVal, t) }));
    const inverses = targets.map((t) => ({ task_id: t.id, patch: col.setValInto(col.getVal(t), t) }));
    setTasks((prev) => { const byId = Object.fromEntries(updates.map((u) => [u.task_id, u.patch])); return prev.map((t) => byId[t.id] ? { ...t, ...byId[t.id] } : t); });
    try {
      const { data } = await api.post("/tasks/bulk-patch-many", { updates });
      const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
      setTasks((prev) => prev.map((t) => byId[t.id] || t));
      toast.success(`Cleared ${data.updated} cell${data.updated === 1 ? "" : "s"}`);
      sheet.pushUndo({
        label: `clear ${col.label}`,
        revert: async () => { try { await api.post("/tasks/bulk-patch-many", { updates: inverses }); } catch {} },
        redo:   async () => { try { await api.post("/tasks/bulk-patch-many", { updates }); } catch {} },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const renderHeaderCell = (col, cIdx) => {
    const isDragOver = dragOverKey === col.key;
    return (
      <th key={col.key} className={`${isDragOver ? "bg-purple-50" : ""}`}
        style={colStyleFor(col)}
        draggable={isAdmin && renamingColKey !== col.key}
        onDragStart={(e) => handleColDragStart(e, col)}
        onDragOver={(e) => handleColDragOver(e, col)}
        onDragLeave={() => setDragOverKey(null)}
        onDrop={(e) => handleColDrop(e, col)}
      >
        <div className="flex items-center gap-1">
          {renamingColKey === col.key && isAdmin ? (
            <ColRenameInput initial={col.label}
              onSave={(v) => commitColRename(col, v)}
              onCancel={() => setRenamingColKey(null)}
              testId={`col-rename-${col.key}`} />
          ) : (
            <span onDoubleClick={() => isAdmin && setRenamingColKey(col.key)}
              className={`flex-1 truncate ${isAdmin ? "cursor-move" : ""}`}
              title={isAdmin ? "Double-click to rename · Drag to reorder" : undefined}>
              {col.label}
            </span>
          )}
          {col.isCustom && <span className="text-[9px] font-mono text-slate-400 lowercase">{col.type}</span>}
          {(isAdmin || canBulk) && renamingColKey !== col.key && (
            <HeaderMenu
              col={col} isAdmin={isAdmin} canBulk={canBulk && col.editable}
              onRename={isAdmin ? () => onColRename(col) : undefined}
              onEditType={col.isCustom ? () => onColEditType(col) : undefined}
              onBulkUpdate={col.editable ? () => setBulkUpdateCol(col) : undefined}
              onClearColumn={col.editable ? () => onColClear(col) : undefined}
              onAddColumn={isAdmin ? () => setColDialog({ mode: "add" }) : undefined}
              onDelete={isAdmin ? () => onColHideOrDelete(col) : undefined}
            />
          )}
        </div>
      </th>
    );
  };

  const renderRowCell = (t, col, r, cIdx, ctx) => {
    const { tdProps, handle } = ctx.cellPropsWithHandle(r, cIdx);
    if (col.isCustom) {
      const rawCol = columns.find((c) => c.id === col.colId);
      if (!rawCol) return null;
      return (
        <CustomCell key={col.key} col={rawCol} value={t.custom?.[col.colId]}
          onSave={(v) => updateCustom(t.id, col.colId, v)} taskId={t.id} members={assignables} lookupMap={userMap}
          tdProps={{ ...tdProps, ["data-testid"]: `cell-custom-${col.colId}-${t.id}` }} handle={handle} />
      );
    }
    const K = col.key;
    if (K === "priority") return (<td key={K} {...tdProps} className={`${tdProps.className || ""} text-center`} data-testid={`cell-priority-${t.id}`}>
      <button
        type="button"
        onClick={() => updateTask(t.id, { priority: !t.priority })}
        aria-label={t.priority ? "Priority: high (click to clear)" : "Priority: normal (click to mark high)"}
        title={t.priority ? "High priority — click to clear" : "Click to mark as high priority"}
        className={`inline-flex h-4 w-4 rounded-full border transition-all ${t.priority ? "bg-red-500 border-red-500 shadow-[0_0_0_2px_rgba(239,68,68,0.15)]" : "bg-slate-200 border-slate-300 hover:bg-slate-300"}`}
        data-testid={`priority-dot-${t.id}`}
      />
      {handle}
    </td>);
    if (K === "date") return <EditableCell key={K} task={t} field="date" value={t.date} onSave={updateTask} editingCell={editingCell} setEditingCell={setEditingCell} type="date" tdProps={{ ...tdProps, ["data-testid"]: `cell-date-${t.id}` }} handle={handle} />;
    if (K === "keyword") return <EditableCell key={K} task={t} field="keyword" value={t.keyword} onSave={updateTask} editingCell={editingCell} setEditingCell={setEditingCell} tdProps={{ ...tdProps, ["data-testid"]: `cell-keyword-${t.id}` }} handle={handle} />;
    if (K === "niche") return <EditableCell key={K} task={t} field="niche" value={t.niche} onSave={updateTask} editingCell={editingCell} setEditingCell={setEditingCell} tdProps={{ ...tdProps, ["data-testid"]: `cell-niche-${t.id}` }} handle={handle} />;
    if (K === "ai_score") return <EditableCell key={K} task={t} field="ai_score" value={t.ai_score} onSave={updateTask} editingCell={editingCell} setEditingCell={setEditingCell} tdProps={{ ...tdProps, ["data-testid"]: `cell-ai_score-${t.id}` }} handle={handle} />;
    if (K === "draft") return (<td key={K} {...tdProps} data-testid={`cell-draft-${t.id}`}>
      {t.draft ? <a href={t.draft} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline text-xs truncate flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Draft</a>
        : <EditableCellInner value="" onSave={(v) => updateTask(t.id, { draft: v })} placeholder="paste URL" />}
      {handle}
    </td>);
    if (K === "method") return (<td key={K} {...tdProps} data-testid={`cell-method-${t.id}`}>
      <MethodSelect value={t.method} onChange={(v) => updateTask(t.id, { method: v })} testId={`method-select-${t.id}`} />
      {handle}
    </td>);
    if (K === "writer_id") return (<td key={K} {...tdProps} data-testid={`cell-writer_id-${t.id}`}>
      <UserSelect users={assignables} value={t.writer_id} onChange={(v) => updateTask(t.id, { writer_id: v })} testId={`writer-select-${t.id}`} lookup={userMap} />
      {handle}
    </td>);
    if (K === "publisher_id") return (<td key={K} {...tdProps} data-testid={`cell-publisher_id-${t.id}`}>
      <UserSelect users={assignables} value={t.publisher_id} onChange={(v) => updateTask(t.id, { publisher_id: v })} testId={`publisher-select-${t.id}`} lookup={userMap} />
      {handle}
    </td>);
    if (K === "status") return (<td key={K} {...tdProps} data-testid={`cell-status-${t.id}`}>
      <StatusSelect value={t.status} onChange={(v) => updateTask(t.id, { status: v })} testId={`status-select-${t.id}`} />
      {handle}
    </td>);
    if (K === "check") return (<td key={K} {...tdProps} className={`${tdProps.className || ""} text-center`} data-testid={`cell-check-${t.id}`}>
      <Checkbox checked={!!t.check} onCheckedChange={(v) => updateTask(t.id, { check: !!v })} data-testid={`check-${t.id}`} />
      {handle}
    </td>);
    if (K === "notes") return <EditableCell key={K} task={t} field="notes" value={t.notes} onSave={updateTask} editingCell={editingCell} setEditingCell={setEditingCell} tdProps={{ ...tdProps, ["data-testid"]: `cell-notes-${t.id}` }} handle={handle} />;
    if (K === "docs") return (<td key={K} {...tdProps} data-testid={`cell-docs-${t.id}`}>
      {t.docs ? <a href={t.docs} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline text-xs flex items-center gap-1" data-testid={`docs-link-${t.id}`}><ExternalLink className="h-3 w-3" /> Open</a>
        : <span className="text-xs text-slate-300">—</span>}
      {handle}
    </td>);
    return null;
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  // Iter 45.21 — Full-width project spreadsheet skeleton (mirrors real
  // layout: header + pipeline bar + spreadsheet rows) instead of the
  // small centered <PageSkeleton>.
  if (!project) return <ProjectViewSkeleton />;

  return (
    <div className="flex-1 min-w-0 flex flex-col overflow-hidden" data-testid="project-page">
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Project</div>
              <div className="flex items-center gap-2 group">
                {editingProjectName ? (
                  <>
                    <input autoFocus value={projectNameDraft} onChange={(e) => setProjectNameDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveProjectName(); if (e.key === "Escape") setEditingProjectName(false); }}
                      onBlur={saveProjectName}
                      className="font-display text-2xl font-bold text-slate-900 border-b-2 border-purple-500 focus:outline-none bg-transparent"
                      data-testid="project-name-input" />
                    <button onClick={saveProjectName} className="text-emerald-600 hover:bg-emerald-50 p-1 rounded"><Check className="h-4 w-4" /></button>
                  </>
                ) : (
                  <>
                    <h1 className="font-display text-2xl font-bold text-slate-900 truncate">{project.name}</h1>
                    {canEditProject && (
                      <button onClick={() => { setEditingProjectName(true); setProjectNameDraft(project.name); }}
                        className="p-1 rounded opacity-0 group-hover:opacity-100 text-slate-400 hover:text-purple-600 transition"
                        data-testid="rename-project-btn"><Pencil className="h-3.5 w-3.5" /></button>
                    )}
                    {canEditProject && (
                      <button onClick={() => setEditProjectOpen(true)}
                        className="p-1 rounded text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition ml-1"
                        title="Edit project details, URL, and assignees"
                        data-testid="edit-project-btn"><Settings2 className="h-4 w-4" /></button>
                    )}
                  </>
                )}
              </div>
              {project.site_url && (
                <a href={project.site_url} target="_blank" rel="noreferrer" className="text-xs text-purple-600 hover:underline flex items-center gap-1 mt-1">
                  <ExternalLink className="h-3 w-3" /> {project.site_url}
                </a>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Collaborator avatar stack (iter 45.25). Google-Sheets-style
                  pile of people who have access to this project. Iter
                  45.26 — clicking now opens an informational popover
                  listing every collaborator with the role they play on
                  this project (Content Manager / Team Lead / Editor /
                  Publisher / Writer), instead of opening the Edit
                  Project dialog. Read-only for everyone. */}
              {collaborators.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center -space-x-1.5 cursor-pointer hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded-full"
                      data-testid="collaborators-stack"
                      aria-label={`${collaborators.length} collaborator${collaborators.length === 1 ? "" : "s"} on this project`}
                    >
                      {collaborators.slice(0, 4).map((u) => (
                        <CollabAvatar key={u.id} user={u} isActive={activeUserIds.has(u.id)} />
                      ))}
                      {collaborators.length > 4 && (
                        <span
                          className="h-7 w-7 rounded-full ring-2 ring-white bg-white border border-slate-300 text-slate-600 flex items-center justify-center text-[10px] font-bold shadow-sm"
                          data-testid="collaborators-more"
                        >
                          +{collaborators.length - 4}
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="w-72 p-0"
                    data-testid="collaborators-popover"
                  >
                    <div className="px-4 py-3 border-b border-slate-100">
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">People with access</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{collaborators.length} member{collaborators.length === 1 ? "" : "s"} on {project.name}</div>
                    </div>
                    <ul className="max-h-72 overflow-auto py-1 scrollbar-thin">
                      {collaborators.map((u) => (
                        <li
                          key={u.id}
                          className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50"
                          data-testid={`collaborator-row-${u.id}`}
                        >
                          <CollabAvatar user={u} isActive={activeUserIds.has(u.id)} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-800 truncate" data-testid={`collaborator-name-${u.id}`}>{u.name}</div>
                            <div className="text-[11px] text-slate-500 truncate" data-testid={`collaborator-role-${u.id}`}>{u.project_role}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </PopoverContent>
                </Popover>
              )}
              {/* View toggle */}
              <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-0.5" data-testid="view-toggle">
                <button
                  onClick={() => setView("list")}
                  className={`px-2.5 py-1 rounded text-xs font-medium flex items-center gap-1.5 transition ${view === "list" ? "bg-white text-purple-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                  data-testid="view-list-btn"
                >
                  <LayoutList className="h-3.5 w-3.5" /> List
                </button>
                <button
                  onClick={() => setView("board")}
                  className={`px-2.5 py-1 rounded text-xs font-medium flex items-center gap-1.5 transition ${view === "board" ? "bg-white text-purple-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                  data-testid="view-board-btn"
                >
                  <KanbanSquare className="h-3.5 w-3.5" /> Board
                </button>
                <button
                  onClick={() => setView("issues")}
                  className={`px-2.5 py-1 rounded text-xs font-medium flex items-center gap-1.5 transition ${view === "issues" ? "bg-white text-rose-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                  data-testid="view-issues-btn"
                >
                  <AlertTriangle className="h-3.5 w-3.5" /> Issues
                  {Object.keys(issuesByTask).length > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold" data-testid="issues-open-badge">
                      {Object.keys(issuesByTask).length}
                    </span>
                  )}
                </button>
              </div>

              {isAdmin && view === "list" && (
                <Button variant="outline" size="sm" onClick={() => setColDialog({ mode: "add" })} data-testid="add-column-btn">
                  <Columns3 className="h-4 w-4 mr-1.5" /> Add column
                </Button>
              )}
              {isAdmin && view === "list" && (colPrefs.hidden || []).length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="hidden-cols-btn">
                      <Eye className="h-4 w-4 mr-1.5" /> Hidden ({colPrefs.hidden.length})
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-56 p-2">
                    <div className="text-xs font-medium text-slate-500 mb-2">Hidden columns</div>
                    <div className="space-y-1">
                      {colPrefs.hidden.map((k) => {
                        const c = columnDefs.find((x) => x.key === k);
                        return (
                          <button key={k} onClick={() => onColRestore(k)} className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded" data-testid={`restore-col-${k}`}>
                            <span className="truncate">{c?.label || k}</span>
                            <span className="text-xs text-purple-600">Show</span>
                          </button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {canCreate && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-paste-btn">
                    <ClipboardPaste className="h-4 w-4 mr-1.5" /> Bulk paste
                  </Button>
                  <AddTaskButton addPosition={addPosition} setAddPosition={setAddPosition} onAdd={addRow} />
                </>
              )}
              {/* Iter 42.5.3 — "Mark Productivity" moved to the global
                  profile menu (bottom-left of the sidebar). See
                  AppLayout.js for the button. The dialog itself hosts a
                  project picker for standalone use, so nothing is lost
                  by removing the per-project shortcut here. "See
                  Productivity" stays because it's project-scoped: it
                  deep-links to the report filtered to THIS project. */}
              {canSeeProductivity && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/productivity?project_id=${id}`)}
                  data-testid="see-productivity-btn"
                >
                  <TrendingUp className="h-4 w-4 mr-1.5" /> See Productivity
                </Button>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search keyword, niche, notes…" className="pl-8 h-8 w-64 text-sm" data-testid="task-search-input" />
            </div>
            <FilterSelect icon={Filter} label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={STATUSES.map((s) => ({ value: s, label: s }))} testId="filter-status" />
            <FilterSelect label="Writer" value={filters.writer_id} onChange={(v) => setFilters({ ...filters, writer_id: v })} options={assignables.map((u) => ({ value: u.id, label: u.name }))} testId="filter-writer" />
            <FilterSelect label="Publisher" value={filters.publisher_id} onChange={(v) => setFilters({ ...filters, publisher_id: v })} options={assignables.map((u) => ({ value: u.id, label: u.name }))} testId="filter-publisher" />
            {activeFilterCount > 0 && (
              <button onClick={() => setFilters({ status: "", writer_id: "", publisher_id: "", urgent: false })} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="clear-filters-btn">
                <X className="h-3 w-3" /> Clear
              </button>
            )}
            <span className="text-xs text-slate-500 ml-auto">{filtered.length} of {tasks.length} tasks</span>
          </div>
        </div>

        {/* Bulk action bar moved to floating footer at the bottom of the screen */}
      </div>

      {/* Pipeline distribution — compact bar on top of tasks (list view only) */}
      {view === "list" && (
        <PipelineBar
          tasks={tasks}
          activeStatus={filters.status}
          onSelectStatus={(s) => setFilters((f) => ({ ...f, status: f.status === s ? "" : s }))}
          urgentActive={filters.urgent}
          onToggleUrgent={() => setFilters((f) => ({ ...f, urgent: !f.urgent }))}
          onRefresh={reload}
          canCreate={canCreate}
          onExportCsv={exportCsv}
          onImportCsv={() => setImportCsvOpen(true)}
        />
      )}

      {/* Shaking issue banners — pinned above the spreadsheet in list view */}
      {view === "list" && tasksWithOpenIssues.length > 0 && (
        <div className="px-6 pt-3 pb-1 bg-white space-y-2" data-testid="issues-banner-strip">
          {tasksWithOpenIssues.slice(0, 5).map((t) => (
            <IssueTaskBanner
              key={t.id}
              task={t}
              issues={issuesByTask[t.id] || []}
              canResolve={canRaiseIssue}
              onResolved={reloadIssues}
              onFocusTask={(taskId) => {
                const row = document.querySelector(`[data-testid="task-row-${taskId}"]`);
                row?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
            />
          ))}
          {tasksWithOpenIssues.length > 5 && (
            <button
              onClick={() => setView("issues")}
              className="text-xs text-rose-700 hover:text-rose-900 underline underline-offset-2"
              data-testid="issues-banner-see-all"
            >
              + {tasksWithOpenIssues.length - 5} more issue{tasksWithOpenIssues.length - 5 === 1 ? "" : "s"} — see all in Issues tab
            </button>
          )}
        </div>
      )}

      {view === "issues" ? (
        <div className="flex-1 overflow-auto scrollbar-thin bg-white">
          <IssueHistoryPanel
            projectId={id}
            tasks={tasks}
            canResolve={canRaiseIssue}
            onChange={reloadIssues}
          />
        </div>
      ) : view === "board" ? (
        <BoardView tasks={filtered} users={users} projects={[project]} onStatusChange={(taskId, status) => updateTask(taskId, { status })} />
      ) : (
        <div ref={scrollContainerRef} className="flex-1 overflow-auto scrollbar-thin smooth-scroll bg-white" data-testid="tasks-scroll-container">
          <table className="spreadsheet-table spreadsheet-table--clickup" data-testid="tasks-table">
            {/* Iter 42 — sticky <thead> freezes the header row while the
                spreadsheet body scrolls beneath it. z-10 keeps it above cell
                outlines / autofill previews. */}
            <thead>
              <tr>
                {canBulk && (
                  <th className="w-10">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-tasks"
                      data-state={allSelected ? "checked" : someSelected ? "indeterminate" : "unchecked"} />
                  </th>
                )}
                <th className="w-8"></th>
                {visibleColumnDefs.map((col, cIdx) => renderHeaderCell(col, cIdx))}
                <th className="w-32">
                  <button
                    onClick={toggleSortByUpdated}
                    className="inline-flex items-center gap-1 hover:text-purple-600 transition font-inherit text-inherit"
                    title={
                      sortByUpdated === "desc" ? "Sorted: newest first · click to sort oldest first"
                      : sortByUpdated === "asc" ? "Sorted: oldest first · click to clear sort"
                      : "Click to sort by last updated (newest first)"
                    }
                    data-testid="sort-last-updated"
                    data-sort-state={sortByUpdated || "off"}
                  >
                    <span>Last Updated</span>
                    {sortByUpdated === "desc" && <ArrowDown className="h-3 w-3 text-purple-600" />}
                    {sortByUpdated === "asc" && <ArrowUp className="h-3 w-3 text-purple-600" />}
                    {!sortByUpdated && <ArrowUpDown className="h-3 w-3 opacity-40" />}
                  </button>
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={4 + visibleColumnDefs.length + (canBulk ? 1 : 0)} className="text-center text-slate-400 py-12">
                  {canBulk ? "No tasks match. Try adjusting filters, add a task, or paste keywords here." : "No tasks match."}
                </td></tr>
              )}
              {/* Iter 42.4 (Perf Round 2) — row virtualization via
                  @tanstack/react-virtual. Only the ~30 visible rows exist
                  in the DOM at once, letting the sheet scale to 10k+
                  tasks without freezing interactions. Padding <tr>s
                  reserve the scroll height so the scrollbar reflects the
                  full dataset. Everything else — sheet-kit range logic,
                  ContextMenu, autofill, drag-select — keeps working
                  because it uses row *indices* (virtualRow.index) that
                  match sheet-kit's original row numbering. */}
              {filtered.length > 0 && virtualPaddingTop > 0 && (
                <tr aria-hidden="true" style={{ height: virtualPaddingTop }} />
              )}
              {filtered.length > 0 && virtualItems.map((virtualRow) => {
                const idx = virtualRow.index;
                const t = filteredSorted[idx];
                if (!t) return null;
                const isSelected = selected.has(t.id);
                const openIssues = issuesByTask[t.id] || [];
                const hasOpenIssue = openIssues.length > 0;
                const AutofillHandle = ({ r, c }) => {
                  if (!sheet.isBottomRightOfRange(r, c)) return null;
                  const col = visibleColumnDefs[c];
                  if (!col || !col.editable) return null;
                  return (
                    <div className="autofill-handle" data-testid={`autofill-handle-${r}-${c}`}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); sheet.beginAutofill(); }} />
                  );
                };
                const cellPropsWithHandle = (r, c, extraClass = "") => {
                  const cp = sheet.cellProps(r, c);
                  return {
                    tdProps: { ...cp, className: `${cp.className} ${extraClass}`.trim(), style: { position: "relative" } },
                    handle: <AutofillHandle r={r} c={c} />,
                  };
                };
                const openCtxAt = (c) => sheet.setFocusFromRow(idx, c);
                const rowTint = (!isSelected && !hasOpenIssue && t.row_color && ROW_COLOR_PALETTE[t.row_color])
                  ? { backgroundColor: ROW_COLOR_PALETTE[t.row_color] }
                  : undefined;
                return (
                  <ContextMenu key={t.id}>
                    <ContextMenuTrigger asChild>
                      <tr data-testid={`task-row-${t.id}`} style={rowTint} className={`${isSelected ? "bg-purple-50/50" : ""} ${hasOpenIssue ? "!bg-rose-50/70" : ""}`}>
                        {canBulk && (
                          <td className="text-center">
                            <Checkbox checked={isSelected} onCheckedChange={() => toggleOne(t.id)} data-testid={`select-task-${t.id}`} />
                          </td>
                        )}
                        <td onMouseDown={(e) => startRowSelect(idx, e)} onMouseEnter={() => dragOverRow(idx)} onMouseOver={() => dragOverRow(idx)}
                          onContextMenu={() => openCtxAt(0)}
                          className={`text-center text-xs font-mono select-none ${canBulk ? "cursor-crosshair" : ""} ${isSelected ? "bg-purple-100 text-purple-700 font-semibold" : "text-slate-400"}`}
                          data-testid={`row-num-${t.id}`}>
                          {idx + 1}
                        </td>
                        {visibleColumnDefs.map((col, cIdx) => renderRowCell(t, col, idx, cIdx, { cellPropsWithHandle }))}
                        <td><LastUpdatedCell task={t} userMap={userMap} /></td>
                        <td className="text-center">
                          {canDelete && (
                            <button onClick={() => removeTask(t.id)} data-testid={`delete-task-${t.id}`} className="p-1 text-slate-400 hover:text-red-600 transition-colors">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="w-52" data-testid={`ctx-menu-${t.id}`}>
                      <ContextMenuItem onSelect={async () => {
                        try {
                          const { data } = await api.post("/tasks", { project_id: id, keyword: "", position: "top", insert_before_id: t.id });
                          setTasks((prev) => { const i = prev.findIndex((x) => x.id === t.id); const next = [...prev]; next.splice(i, 0, data); return next; });
                          let liveId = data.id;
                          sheet.pushUndo({
                            label: "insert row",
                            revert: async () => { try { await api.delete(`/tasks/${liveId}`); } catch {} setTasks((prev) => prev.filter((x) => x.id !== liveId)); },
                            redo: async () => {
                              try {
                                const { data: recreated } = await api.post("/tasks", { project_id: id, keyword: "", position: "top", insert_before_id: t.id });
                                if (recreated && recreated.id) liveId = recreated.id;
                              } catch {}
                              reload();
                            },
                          });
                        } catch { await api.post("/tasks", { project_id: id, keyword: "", position: "top" }); reload(); }
                      }} data-testid={`ctx-insert-above-${t.id}`}>
                        <ArrowUp className="h-3.5 w-3.5 mr-2" /> Insert row above
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={async () => {
                        try {
                          const { data } = await api.post("/tasks", { project_id: id, keyword: "", position: "bottom" });
                          setTasks((prev) => [...prev, data]);
                          let liveId = data.id;
                          sheet.pushUndo({
                            label: "insert row",
                            revert: async () => { try { await api.delete(`/tasks/${liveId}`); } catch {} setTasks((prev) => prev.filter((x) => x.id !== liveId)); },
                            redo: async () => {
                              try {
                                const { data: recreated } = await api.post("/tasks", { project_id: id, keyword: "", position: "bottom" });
                                if (recreated && recreated.id) liveId = recreated.id;
                              } catch {}
                              reload();
                            },
                          });
                        } catch { reload(); }
                      }} data-testid={`ctx-insert-below-${t.id}`}>
                        <ArrowDown className="h-3.5 w-3.5 mr-2" /> Insert row below
                      </ContextMenuItem>
                      <ContextMenuSeparator />
                      <ContextMenuItem onSelect={() => sheet.copySelection()} data-testid={`ctx-copy-${t.id}`}>
                        <Copy className="h-3.5 w-3.5 mr-2" /> Copy
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={async () => {
                        try { const txt = await navigator.clipboard.readText(); if (txt) await sheet.pasteAtFocus(txt); }
                        catch { toast.error("Clipboard read blocked — use Ctrl+V"); }
                      }} data-testid={`ctx-paste-${t.id}`}>
                        <ClipboardPaste className="h-3.5 w-3.5 mr-2" /> Paste
                      </ContextMenuItem>
                      <ContextMenuItem onSelect={() => sheet.clearRange()} data-testid={`ctx-clear-${t.id}`}>
                        <X className="h-3.5 w-3.5 mr-2" /> Clear cells
                      </ContextMenuItem>
                      {canRaiseIssue && (
                        <>
                          <ContextMenuSeparator />
                          <ContextMenuItem
                            onSelect={() => setRaiseIssueFor(t)}
                            className="text-rose-700 focus:text-rose-800 focus:bg-rose-50"
                            data-testid={`ctx-raise-issue-${t.id}`}
                          >
                            <AlertTriangle className="h-3.5 w-3.5 mr-2" /> Raise issue
                          </ContextMenuItem>
                        </>
                      )}
                      <ContextMenuSeparator />
                      <ContextMenuItem onSelect={async () => {
                        // If a range spans multiple rows, delete all of them.
                        // Iter 42 — ALWAYS confirm before deleting from the
                        // context menu (including single-row). Fixes an
                        // "accidental delete on right-click at bottom of
                        // list" report where the Radix menu opened above the
                        // cursor and the Delete item landed under the
                        // pointer, triggering an immediate delete on mouse-up.
                        const rangeIds = sheet.rangeTaskIds();
                        const ids = rangeIds.length > 1 ? rangeIds : [t.id];
                        const label = ids.length === 1 ? "Delete this row?" : `Delete ${ids.length} rows?`;
                        if (!confirm(label)) return;
                        if (ids.length === 1) { await removeTask(ids[0]); return; }
                        const snapshots = tasks.filter((x) => ids.includes(x.id));
                        let liveIds = [...ids];
                        try {
                          await api.post("/tasks/bulk-delete", { task_ids: liveIds });
                          setTasks((prev) => prev.filter((x) => !liveIds.includes(x.id)));
                          setSelected((prev) => { const n = new Set(prev); liveIds.forEach((tid) => n.delete(tid)); return n; });
                          toast.success(`Deleted ${liveIds.length} tasks`);
                          sheet.pushUndo({
                            label: `delete ${snapshots.length} tasks`,
                            revert: async () => {
                              const newIds = [];
                              for (const s of snapshots) {
                                const { id: _oid, created_at, updated_at, order, ...rest } = s;
                                try {
                                  const { data: created } = await api.post("/tasks", { ...rest, project_id: id, position: "bottom" });
                                  if (created && created.id) newIds.push(created.id);
                                } catch {}
                              }
                              if (newIds.length) liveIds = newIds;
                              reload();
                            },
                            redo: async () => {
                              try { await api.post("/tasks/bulk-delete", { task_ids: liveIds }); } catch {}
                              reload();
                            },
                          });
                        } catch (e) { toast.error(formatApiError(e)); }
                      }} className="text-red-600 focus:text-red-700 focus:bg-red-50" data-testid={`ctx-delete-${t.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-2" />
                        {sheet.rangeTaskIds().length > 1 ? `Delete ${sheet.rangeTaskIds().length} rows` : "Delete row"}
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenu>
                );
              })}
              {/* Iter 42.4 — bottom spacer to reserve the remaining scroll
                  height. Same idea as the top padding row above. */}
              {filtered.length > 0 && virtualPaddingBottom > 0 && (
                <tr aria-hidden="true" style={{ height: virtualPaddingBottom }} />
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Bulk paste dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Bulk add keywords</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto scrollbar-thin">
            <p className="text-sm text-slate-600">Paste keywords, one per line. Tab-separated columns are supported (only first is used).</p>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Insert at</Label>
              <RadioGroup value={bulkPosition} onValueChange={setBulkPosition} className="flex gap-4 mt-2" data-testid="bulk-position">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="top" /> Top of the list
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="bottom" /> Bottom of the list
                </label>
              </RadioGroup>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 uppercase">Apply to all pasted tasks</span>
                <span className="text-[10px] text-slate-400">(optional)</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={bulkDefaults.status || "keep"} onValueChange={(v) => setBulkDefaults({...bulkDefaults, status: v === "keep" ? "" : v})}>
                    <SelectTrigger className="h-8 text-xs" data-testid="bulk-default-status"><SelectValue placeholder="To-Do" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keep">To-Do (default)</SelectItem>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Method</Label>
                  <Select value={bulkDefaults.method || "none"} onValueChange={(v) => setBulkDefaults({...bulkDefaults, method: v === "none" ? "" : v})}>
                    <SelectTrigger className="h-8 text-xs" data-testid="bulk-default-method"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— (leave blank)</SelectItem>
                      {METHOD_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Writer</Label>
                  <Select value={bulkDefaults.writer_id || "none"} onValueChange={(v) => setBulkDefaults({...bulkDefaults, writer_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="h-8 text-xs" data-testid="bulk-default-writer"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {assignables.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Publisher</Label>
                  <Select value={bulkDefaults.publisher_id || "none"} onValueChange={(v) => setBulkDefaults({...bulkDefaults, publisher_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="h-8 text-xs" data-testid="bulk-default-publisher"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {assignables.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Niche</Label>
                  <Input value={bulkDefaults.niche || ""} onChange={(e) => setBulkDefaults({...bulkDefaults, niche: e.target.value})}
                    className="h-8 text-xs" placeholder="e.g. Tech, Fitness…" data-testid="bulk-default-niche" />
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={bulkDefaults.date || ""} onChange={(e) => setBulkDefaults({...bulkDefaults, date: e.target.value})}
                    className="h-8 text-xs" data-testid="bulk-default-date" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Keywords</Label>
              <Textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={8} className="font-mono text-sm mt-2"
                placeholder={`best budget laptops\nseo tips for beginners\nhow to write a blog post`}
                data-testid="bulk-paste-textarea" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={handleBulk} className="bg-purple-600 hover:bg-purple-700" data-testid="bulk-submit-btn">Add tasks</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column dialog */}
      {colDialog && (
        <ColumnDialog dialog={colDialog} projectId={id} projectDept={project.department || "content"} onClose={() => setColDialog(null)} onDone={() => { setColDialog(null); reload(); }} />
      )}
      <BulkUpdateColumnDialog
        open={!!bulkUpdateCol}
        onOpenChange={(o) => !o && setBulkUpdateCol(null)}
        col={bulkUpdateCol}
        filteredCount={filtered.length}
        onApply={(val, limit) => applyBulkUpdate(bulkUpdateCol, val, limit)}
      />

      <ImportCSVDialog
        open={importCsvOpen}
        onClose={() => setImportCsvOpen(false)}
        columnDefs={visibleColumnDefs}
        context={{ userMap }}
        onImport={importCsv}
      />

      <EditProjectDialog
        open={editProjectOpen}
        onClose={() => setEditProjectOpen(false)}
        project={project}
        users={users}
        currentUser={user}
        onSaved={reload}
      />

      {/* Floating bulk action bar at bottom of screen — liquid glass, soft
          purple. Shows when at least one row is selected AND the user can
          either bulk-edit OR raise an issue (publishers/publishing TLs
          without full bulk permission still get the Raise-Issue button). */}
      {(canBulk || canRaiseIssue) && selected.size > 0 && view === "list" && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 liquid-glass-purple rounded-2xl flex items-center gap-2 px-4 py-2.5 max-w-[calc(100vw-2rem)] overflow-x-auto scrollbar-thin"
             data-testid="bulk-action-bar">
          <div className="flex items-center gap-2 pr-3 border-r lg-divider shrink-0">
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-xs font-bold text-white shadow-inner">
              {selected.size}
            </div>
            <span className="text-sm font-semibold whitespace-nowrap text-slate-100">selected</span>
          </div>
          {canBulk && (
            <>
              <Select onValueChange={(v) => bulkPatch({ status: v })}>
                <SelectTrigger className="h-8 text-xs w-40 lg-btn" data-testid="bulk-set-status">
                  <SelectValue placeholder="Set status…" />
                </SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Select onValueChange={(v) => bulkPatch({ method: v })}>
                <SelectTrigger className="h-8 text-xs w-32 lg-btn" data-testid="bulk-set-method">
                  <SelectValue placeholder="Set method…" />
                </SelectTrigger>
                <SelectContent>{METHOD_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Select onValueChange={(v) => bulkPatch({ writer_id: v })}>
                <SelectTrigger className="h-8 text-xs w-40 lg-btn" data-testid="bulk-set-writer">
                  <SelectValue placeholder="Assign writer…" />
                </SelectTrigger>
                <SelectContent>
                  {writerPool.length === 0 ? (
                    <div className="px-2 py-1.5 text-[11px] text-slate-400 italic">Assign writers to this project first</div>
                  ) : writerPool.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select onValueChange={(v) => bulkPatch({ publisher_id: v })}>
                <SelectTrigger className="h-8 text-xs w-40 lg-btn" data-testid="bulk-set-publisher">
                  <SelectValue placeholder="Assign publisher…" />
                </SelectTrigger>
                <SelectContent>
                  {publisherPool.length === 0 ? (
                    <div className="px-2 py-1.5 text-[11px] text-slate-400 italic">Assign publishers to this project first</div>
                  ) : publisherPool.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {/* Iter 45.26 — Row color picker. Tints the selected rows in
                  the spreadsheet (Google Sheets style). Empty key clears. */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 lg-btn text-xs font-medium text-slate-100 hover:text-white"
                    data-testid="bulk-set-row-color-trigger"
                  >
                    <Palette className="h-3.5 w-3.5 mr-1" /> Color
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2" side="top" align="center">
                  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1 pb-1.5">
                    Row color
                  </div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {ROW_COLOR_SWATCHES.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        title={s.label}
                        onClick={() => bulkPatch({ row_color: s.key })}
                        className="h-7 w-7 rounded-md border border-slate-300 hover:scale-110 hover:ring-2 hover:ring-purple-300 transition-transform"
                        style={{ backgroundColor: ROW_COLOR_PALETTE[s.key] }}
                        data-testid={`row-color-swatch-${s.key}`}
                      />
                    ))}
                    <button
                      type="button"
                      title="Clear color"
                      onClick={() => bulkPatch({ row_color: "" })}
                      className="h-7 w-7 rounded-md border border-slate-300 bg-white hover:scale-110 hover:ring-2 hover:ring-purple-300 transition-transform flex items-center justify-center col-span-2"
                      data-testid="row-color-swatch-clear"
                    >
                      <X className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-[11px] text-slate-600 ml-1">Clear</span>
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
            </>
          )}
          {canRaiseIssue && selected.size === 1 && (
            <Button
              size="sm"
              className="h-8 bg-rose-500/90 hover:bg-rose-600 text-white shadow-sm border border-rose-300/50"
              onClick={() => {
                const [taskId] = Array.from(selected);
                const t = tasks.find((x) => x.id === taskId);
                if (t) setRaiseIssueFor(t);
              }}
              data-testid="raise-issue-btn"
            >
              <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Raise issue
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="destructive" className="h-8" onClick={bulkDelete} data-testid="bulk-delete-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          )}
          <button
            onClick={() => setSelected(new Set())}
            className="p-1.5 rounded-md lg-btn"
            data-testid="clear-selection"
            title="Clear selection"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Raise-Issue dialog */}
      <RaiseIssueDialog
        open={!!raiseIssueFor}
        onOpenChange={(o) => !o && setRaiseIssueFor(null)}
        task={raiseIssueFor}
        onCreated={() => { setRaiseIssueFor(null); reloadIssues(); }}
        project={project}
        users={users}
      />
    </div>
  );
}

function PipelineBar({ tasks, activeStatus, onSelectStatus, urgentActive, onToggleUrgent, onRefresh, canCreate, onExportCsv, onImportCsv }) {
  const total = tasks.length;
  const counts = STATUSES.map((s) => ({ s, c: tasks.filter((t) => t.status === s).length }));
  // Iter 45.39 — Urgent quick-filter pill: same visual language as the
  // status chips but red-toned, sits at the front of the row.
  const urgentCount = tasks.filter((t) => t.priority).length;
  if (total === 0) return null;

  return (
    <div className="border-b border-slate-200 bg-white" data-testid="project-pipeline-bar">
      <div className="px-6 py-3">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Pipeline</span>
          <div className="flex-1 h-2 rounded-full overflow-hidden bg-slate-100 flex">
            {counts.map(({ s, c }) => {
              const pct = (c / total) * 100;
              if (pct === 0) return null;
              const isActive = activeStatus === s;
              return (
                <button
                  key={s}
                  onClick={() => onSelectStatus(s)}
                  className={`${STATUS_META_LOCAL[s].bar} transition hover:opacity-90 ${isActive ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
                  style={{ width: `${pct}%` }}
                  title={`${s}: ${c}`}
                  data-testid={`project-pipeline-bar-${s}`}
                />
              );
            })}
          </div>
          <span className="text-xs text-slate-500 tabular-nums">{total} tasks</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Iter 45.39 — Urgent pill (leftmost). Disabled state when
              no tasks are currently marked priority. */}
          <button
            onClick={() => urgentCount > 0 && onToggleUrgent && onToggleUrgent()}
            disabled={urgentCount === 0}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-semibold transition ${
              urgentCount === 0
                ? "border-slate-100 bg-slate-50 text-slate-300 cursor-default"
                : urgentActive
                ? "bg-red-600 text-white border-red-600 shadow-sm"
                : "bg-red-50 text-red-700 border-red-200 hover:brightness-95"
            }`}
            data-testid="project-urgent-chip"
            data-active={urgentActive || undefined}
            title={urgentCount === 0 ? "No urgent tasks" : urgentActive ? "Showing only urgent tasks — click to clear" : "Filter to urgent tasks only"}
          >
            <AlertTriangle className={`h-3 w-3 ${urgentActive ? "" : urgentCount === 0 ? "" : "text-red-500"}`} />
            Urgent
            <span className={`font-mono font-bold ${urgentActive ? "" : "opacity-80"}`}>{urgentCount}</span>
          </button>
          {counts.map(({ s, c }) => {
            const M = STATUS_META_LOCAL[s];
            const isActive = activeStatus === s;
            const isEmpty = c === 0;
            return (
              <button
                key={s}
                onClick={() => !isEmpty && onSelectStatus(s)}
                disabled={isEmpty}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition ${
                  isEmpty
                    ? "border-slate-100 bg-slate-50 text-slate-300 cursor-default"
                    : isActive
                    ? `${M.active} shadow-sm`
                    : `${M.cls} hover:brightness-95`
                }`}
                data-testid={`project-status-chip-${s}`}
                data-active={isActive || undefined}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${M.bar}`} />
                {s}
                <span className={`font-mono font-bold ${isActive ? "" : "opacity-80"}`}>{c}</span>
              </button>
            );
          })}
          {onRefresh && (
            <div className="ml-auto flex items-center gap-2">
              {canCreate && (
                <>
                  <button
                    onClick={onExportCsv}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium transition shadow-sm"
                    data-testid="export-csv-btn"
                    title="Export current filtered tasks as CSV"
                  >
                    <Download className="h-3.5 w-3.5" /> Export CSV
                  </button>
                  <button
                    onClick={onImportCsv}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium transition shadow-sm"
                    data-testid="import-csv-btn"
                    title="Import tasks from a CSV file"
                  >
                    <Upload className="h-3.5 w-3.5" /> Import CSV
                  </button>
                </>
              )}
              <RefreshControl onRefresh={onRefresh} storageKey="projectAutoRefreshMs" testId="project-refresh" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const STATUS_META_LOCAL = {
  "To-Do":              { bar: "bg-slate-400",   cls: "bg-slate-100 text-slate-700 border-slate-200",    active: "bg-slate-800 text-white border-slate-800" },
  "Editing Pending":    { bar: "bg-amber-500",   cls: "bg-amber-100 text-amber-700 border-amber-200",    active: "bg-amber-600 text-white border-amber-600" },
  "Publishing Pending": { bar: "bg-orange-500",  cls: "bg-orange-100 text-orange-700 border-orange-200", active: "bg-orange-600 text-white border-orange-600" },
  "Review Pending":     { bar: "bg-blue-500",    cls: "bg-blue-100 text-blue-700 border-blue-200",       active: "bg-blue-700 text-white border-blue-700" },
  "Published":          { bar: "bg-emerald-500", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", active: "bg-emerald-700 text-white border-emerald-700" },
};

function AddTaskButton({ addPosition, setAddPosition, onAdd }) {
  return (
    <div className="flex items-stretch">
      <Button size="sm" onClick={onAdd} className="bg-purple-600 hover:bg-purple-700 rounded-r-none" data-testid="add-task-btn">
        <Plus className="h-4 w-4 mr-1.5" /> Add task
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700 rounded-l-none border-l border-purple-500 px-2" data-testid="add-task-position-btn" title={`New task position: ${addPosition}`}>
            {addPosition === "top" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
            <ChevronDown className="h-3 w-3 ml-0.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-52 p-2" data-testid="add-task-position-menu">
          <div className="text-[10px] font-semibold text-slate-500 uppercase px-2 pb-1.5">Add new task at</div>
          <button
            onClick={() => setAddPosition("top")}
            data-testid="pos-top"
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-slate-100 ${addPosition === "top" ? "text-purple-700 font-medium" : "text-slate-700"}`}
          >
            <ArrowUp className="h-3.5 w-3.5" /> Top of the list
            {addPosition === "top" && <Check className="h-3.5 w-3.5 ml-auto" />}
          </button>
          <button
            onClick={() => setAddPosition("bottom")}
            data-testid="pos-bottom"
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-slate-100 ${addPosition === "bottom" ? "text-purple-700 font-medium" : "text-slate-700"}`}
          >
            <ArrowDown className="h-3.5 w-3.5" /> Bottom of the list
            {addPosition === "bottom" && <Check className="h-3.5 w-3.5 ml-auto" />}
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ColumnHeader({ col, isAdmin, onEdit, onDelete, projectDept, canBulk, onBulkUpdate, onAddColumn }) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(col.label);
  useEffect(() => setDraft(col.label), [col.label]);
  const save = async () => {
    const label = draft.trim();
    setRenaming(false);
    if (!label || label === col.label) return;
    try {
      await api.patch(`/departments/${projectDept || "content"}/columns/${col.id}`, { label });
      toast.success("Column renamed everywhere");
      window.dispatchEvent(new CustomEvent("columns-changed"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <th className="min-w-[140px]">
      <div className="flex items-center gap-1">
        {renaming && isAdmin ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(col.label); setRenaming(false); } }}
            className="flex-1 text-xs px-1 py-0.5 border border-purple-500 rounded bg-white"
            data-testid={`col-rename-${col.id}`}
          />
        ) : (
          <span
            onDoubleClick={() => isAdmin && setRenaming(true)}
            className={`flex-1 truncate ${isAdmin ? "cursor-text" : ""}`}
            title={isAdmin ? "Double-click to rename" : undefined}
          >
            {col.label}
          </span>
        )}
        <span className="text-[9px] font-mono text-slate-400 lowercase">{col.type}</span>
        {(isAdmin || canBulk) && !renaming && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid={`col-menu-${col.id}`}>
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 p-1">
              {isAdmin && (
                <button onClick={() => setRenaming(true)} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded" data-testid={`col-rename-btn-${col.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Rename column
                </button>
              )}
              {isAdmin && (
                <button onClick={onEdit} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded" data-testid={`col-edit-${col.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Edit type / options
                </button>
              )}
              {canBulk && onBulkUpdate && (
                <button onClick={onBulkUpdate} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded" data-testid={`col-bulk-update-${col.id}`}>
                  <Columns3 className="h-3.5 w-3.5" /> Bulk update column
                </button>
              )}
              {isAdmin && onAddColumn && (
                <button onClick={onAddColumn} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-100 rounded" data-testid={`col-add-${col.id}`}>
                  <Plus className="h-3.5 w-3.5" /> Add new column
                </button>
              )}
              {isAdmin && (
                <button onClick={onDelete} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded" data-testid={`col-delete-${col.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete column
                </button>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>
    </th>
  );
}

function ColumnDialog({ dialog, projectId, projectDept, onClose, onDone }) {
  const editing = dialog.mode === "edit";
  const [label, setLabel] = useState(dialog.col?.label || "");
  const [type, setType] = useState(dialog.col?.type || "text");
  const [optionsText, setOptionsText] = useState((dialog.col?.options || []).join("\n"));

  const submit = async () => {
    if (!label.trim()) { toast.error("Label required"); return; }
    const payload = { label: label.trim(), type };
    if (type === "dropdown") {
      const options = optionsText.split("\n").map((s) => s.trim()).filter(Boolean);
      if (options.length === 0) { toast.error("Add at least one dropdown option"); return; }
      payload.options = options;
    } else {
      payload.options = [];
    }
    const dept = projectDept || "content";
    try {
      if (editing) {
        await api.patch(`/departments/${dept}/columns/${dialog.col.id}`, payload);
        toast.success("Column updated everywhere");
      } else {
        await api.post(`/departments/${dept}/columns`, payload);
        toast.success("Column added to every project in this department");
      }
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit column" : "Add column to Content Team"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-purple-50 border border-purple-100 px-3 py-2 text-xs text-purple-700">
            Columns are shared across every project in the Content Team department. Only admins can add, edit, or remove them.
          </div>
          <div>
            <Label>Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Word Count, Assignee, Priority" data-testid="col-label-input" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="col-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="dropdown">Dropdown</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="url">Link</SelectItem>
              </SelectContent>
            </Select>
            {type === "member" && (
              <p className="text-[11px] text-slate-500 mt-1">Values will be picked from team members (associates and team leads).</p>
            )}
            {type === "url" && (
              <p className="text-[11px] text-slate-500 mt-1">Cells accept a URL (e.g. Google Doc, drive link). Once set, the cell renders as a clickable link.</p>
            )}
          </div>
          {type === "dropdown" && (
            <div>
              <Label>Dropdown options (one per line)</Label>
              <Textarea rows={5} value={optionsText} onChange={(e) => setOptionsText(e.target.value)}
                placeholder={"Low\nMedium\nHigh"} data-testid="col-options-input" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-purple-600 hover:bg-purple-700" data-testid="col-submit">
            {editing ? "Save changes" : "Add column"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LastUpdatedCell({ task, userMap }) {
  // Iter 42.9 — history is no longer shipped in the list payload. We fetch
  // it lazily the first time the popover opens and cache in local state so
  // subsequent opens are instant. Prevents multi-MB payloads on projects
  // where tasks have long status histories.
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(Array.isArray(task.status_history) ? task.status_history : null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (open && history === null && !loading) {
      setLoading(true);
      api.get(`/tasks/${task.id}/history`)
        .then((r) => setHistory(r.data?.status_history || []))
        .catch(() => setHistory([]))
        .finally(() => setLoading(false));
    }
  }, [open, task.id, history, loading]);
  const orderedHistory = Array.isArray(history) ? [...history].reverse() : [];
  const label = task.updated_at ? formatRelative(task.updated_at) : "—";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-purple-600 hover:underline w-full" data-testid={`last-updated-${task.id}`}>
          <History className="h-3 w-3 text-slate-400" /><span>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Status History</div>
        {loading && <div className="text-sm text-slate-400">Loading…</div>}
        {!loading && orderedHistory.length === 0 && <div className="text-sm text-slate-400">No status changes yet.</div>}
        <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin">
          {orderedHistory.map((h, i) => (
            <div key={i} className="text-xs border-l-2 border-purple-200 pl-3">
              <div className="font-medium text-slate-900">
                <span className="text-slate-500">{h.from || "—"}</span> → <span className="text-purple-700">{h.to}</span>
              </div>
              <div className="text-slate-500 mt-0.5">{h.by_name || userMap[h.by_id]?.name || "Unknown"} · {formatDateTime(h.at)}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-2 border-t border-slate-100 text-[11px] text-slate-400">
          Last modified: {formatDateTime(task.updated_at)}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function EditableCell({ task, field, value, onSave, editingCell, setEditingCell, type, tdProps = {}, handle }) {
  const isEditing = editingCell?.taskId === task.id && editingCell?.field === field;
  const [local, setLocal] = useState(value || "");
  useEffect(() => { setLocal(value || ""); }, [value]);
  const { className: extraClass = "", onClick: sheetOnClick, ...restTd } = tdProps;
  if (isEditing) {
    return (
      <td className={`editing ${extraClass}`} {...restTd}>
        <input autoFocus type={type || "text"} value={local} onChange={(e) => setLocal(e.target.value)}
          onBlur={() => { setEditingCell(null); if (local !== value) onSave(task.id, { [field]: local }); }}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setLocal(value || ""); setEditingCell(null); } }}
          data-testid={`edit-${field}-${task.id}`} />
        {handle}
      </td>
    );
  }
  // Iter 42 — single-click enters edit mode (Excel-like). We still run
  // sheet-kit's onClick to set focus first, then enter edit mode on the
  // same tick. Fixes "cannot edit a specific cell after bulk updating the
  // same column" (bulk update leaves the cells focus-stuck; single click
  // used to only re-focus without ever opening the editor).
  return (
    <td
      {...restTd}
      onClick={(e) => { sheetOnClick?.(e); setEditingCell({ taskId: task.id, field }); }}
      className={`cursor-text ${extraClass}`}
    >
      {value ? <span className="text-slate-800">{value}</span> : <span className="text-slate-300 text-xs italic">click to edit</span>}
      {handle}
    </td>
  );
}

function EditableCellInner({ value, onSave, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value || "");
  if (editing) {
    return (
      <input autoFocus value={local} onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { setEditing(false); if (local !== value) onSave(local); }}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className="w-full text-xs px-1 py-0.5 border border-purple-500 rounded" />
    );
  }
  return <button onClick={() => setEditing(true)} className="text-xs text-slate-300 italic hover:text-slate-500">{placeholder}</button>;
}

/* Iter 45.35 — MiniAvatar is a 20-px round avatar used inside the
   Writer / Publisher dropdown triggers and menu items.  It reuses the
   same deterministic tint from the CollabAvatar palette so a
   teammate's colour is consistent everywhere in the app. */
function MiniAvatar({ user }) {
  const initials = (user?.name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const tint = COLLAB_PALETTE[_hash(user?.id || user?.name || "") % COLLAB_PALETTE.length];
  if (user?.avatar_url) {
    return <img src={user.avatar_url} alt={user.name} className="w-5 h-5 rounded-full object-cover ring-1 ring-white shadow-sm flex-shrink-0" />;
  }
  return (
    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ring-1 ring-white shadow-sm flex-shrink-0 ${tint}`}>
      {initials}
    </span>
  );
}

function UserSelect({ users, value, onChange, testId, lookup }) {
  // Iter 45.76 — `lookup` is the page's full user map; it keeps the assigned
  // name rendering even after the person is removed from the project (they
  // drop out of `users`, the project-scoped options list).
  const selected = users.find((u) => u.id === value) || (value ? lookup?.[value] : null);
  return (
    <Select value={value || "unassigned"} onValueChange={(v) => onChange(v === "unassigned" ? null : v)}>
      <SelectTrigger
        className="h-7 text-xs border-transparent hover:border-slate-200 bg-transparent gap-2 px-1.5"
        data-testid={testId}
      >
        {selected ? (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <MiniAvatar user={selected} />
            <span className="truncate text-slate-700 font-medium text-xs">{selected.name}</span>
          </div>
        ) : (
          <span className="text-slate-400 italic">Unassigned</span>
        )}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="unassigned">
          <span className="text-slate-500 italic text-xs">Unassigned</span>
        </SelectItem>
        {users.map((u) => (
          <SelectItem key={u.id} value={u.id} disabled={u.is_active === false}>
            <span className="flex items-center gap-2">
              <MiniAvatar user={u} />
              <span className="text-xs">{u.name}{u.is_active === false ? " (inactive)" : ""}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function StatusSelect({ value, onChange, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className={`h-7 text-xs font-semibold border rounded-full px-2.5 gap-1.5 transition-colors ${STATUS_CLS[value] || ""}`}
        data-testid={testId}
      >
        {/* Iter 45.36 — Manual trigger content (no <SelectValue>) so
            we render exactly ONE dot; Radix's SelectValue was
            re-rendering the SelectItem's dot+text JSX in the trigger,
            which produced the "2 dots" bug the user flagged. */}
        <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[value] || "bg-slate-400"}`} />
        <span className="truncate">{value || ""}</span>
      </SelectTrigger>
      <SelectContent>
        {STATUSES.map((s) => (
          <SelectItem key={s} value={s}>
            <span className="flex items-center gap-2">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${STATUS_DOT[s]}`} />
              <span className="text-xs">{s}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MethodSelect({ value, onChange, testId }) {
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger className="h-7 text-xs border-transparent hover:border-slate-200 bg-transparent" data-testid={testId}>
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">—</SelectItem>
        {METHOD_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function FilterSelect({ label, value, onChange, options, icon: Icon, testId }) {
  return (
    <Select value={value || "any"} onValueChange={(v) => onChange(v === "any" ? "" : v)}>
      <SelectTrigger className="h-8 text-xs w-auto min-w-[130px]" data-testid={testId}>
        {Icon && <Icon className="h-3 w-3 mr-1.5" />}
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="any">All {label}s</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// ---------- Header 3-dot menu + Bulk-update-column dialog imported from SheetHeaderKit ----------

function ColRenameInput({ initial, onSave, onCancel, testId }) {
  const [v, setV] = useState(initial || "");
  return (
    <input autoFocus type="text" value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onSave(v)}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") onCancel(); }}
      className="flex-1 text-xs px-1 py-0.5 border border-purple-500 rounded bg-white"
      data-testid={testId} />
  );
}
